// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.

export const routes = [
  {
    path: '/quotes',
    name: 'quotes',
    component: () => import('./pages/QuotesPage.vue'),
    meta: { permission: 'quotes:read', title: 'quotes.title' , ownsQuery: true /* limpia la consulta después de leerla: el armazón no la remonta al cambiar */ },
  },
  // The editor covers the shell (`position: fixed`) rather than sitting inside
  // it: writing a quote is thirty lines of six columns, a catalogue to pull
  // from and a conversation running alongside, and none of that fits next to a
  // sidebar. It is still a route and not a modal, so a half-written quote can
  // be linked to, reloaded and come back from the browser's back button.
  {
    path: '/quotes/new',
    name: 'quote-new',
    component: () => import('./pages/QuoteEditorPage.vue'),
    meta: { permission: 'quotes:write', title: 'quotes.form.newTitle', fullScreen: true },
  },
  {
    path: '/quotes/catalog',
    name: 'quotes-catalog',
    component: () => import('./pages/CatalogPage.vue'),
    meta: { permission: 'quotes:read', title: 'quotes.catalog.title' },
  },
  {
    // Igual que el editor de plantillas y el de reglas: `new` y un id comparten
    // ruta porque comparten pantalla.
    //
    // Estuvo en un modal con veintinueve campos dentro. En un telefono eso era
    // una ventana con scroll propio dentro de una pagina con scroll propio, con
    // el teclado tapando la mitad de lo poco que se veia.
    path: '/quotes/catalog/:id',
    name: 'quotes-rate-item',
    component: () => import('./pages/RateItemEditorPage.vue'),
    meta: { permission: 'quotes:catalog', title: 'quotes.catalog.title' },
  },
  {
    path: '/quotes/templates',
    name: 'quotes-templates',
    component: () => import('./pages/TemplatesPage.vue'),
    meta: { permission: 'quotes:read', title: 'quotes.templates.title' },
  },
  {
    // `new` and an id share one route because they share one editor. Splitting
    // them would mean two routes pointing at the same component with the same
    // props, differing only in a word.
    path: '/quotes/templates/:id',
    name: 'quotes-template',
    component: () => import('./pages/TemplateEditorPage.vue'),
    meta: { permission: 'quotes:knowledge', title: 'quotes.templates.title', fullScreen: true },
  },
  {
    path: '/quotes/rules',
    name: 'quotes-rules',
    component: () => import('./pages/RulesPage.vue'),
    meta: { permission: 'quotes:read', title: 'quotes.rules.title' },
  },
  {
    // Igual que el editor de plantillas: `new` y un id comparten ruta porque
    // comparten pantalla, y lo único que cambia es una palabra del título.
    //
    // Escribir una regla estuvo en un modal y ahí no cabía: son cinco campos y
    // cuatro son párrafos, y en un teléfono eso es escribir por una rendija con
    // el teclado tapando la mitad. Con dirección propia además se puede volver
    // atrás, guardar el enlace y mandárselo a un compañero.
    path: '/quotes/rules/:id',
    name: 'quotes-rule',
    component: () => import('./pages/RuleEditorPage.vue'),
    meta: { permission: 'quotes:knowledge', title: 'quotes.rules.title' },
  },
  {
    path: '/quotes/learning',
    name: 'quotes-learning',
    component: () => import('./pages/LearningPage.vue'),
    meta: { permission: 'quotes:knowledge', title: 'quotes.learning.title' },
  },
  // Last, so the static paths above are matched first. Vue Router ranks static
  // segments over dynamic ones regardless, but relying on that silently is how
  // /quotes/catalog ends up opening a quote whose id is "catalog".
  {
    path: '/quotes/:id',
    name: 'quote',
    component: () => import('./pages/QuotePage.vue'),
    meta: { permission: 'quotes:read', title: 'quotes.detail.title' },
  },
  {
    path: '/quotes/:id/edit',
    name: 'quote-edit',
    component: () => import('./pages/QuoteEditorPage.vue'),
    meta: { permission: 'quotes:write', title: 'quotes.form.editTitle', fullScreen: true },
  },
]

/**
 * One entry in the sidebar, with the module's other screens folded under it.
 *
 * This module has five screens and the sidebar has room for about eight in
 * total. Five flat entries would push whatever is installed next below the
 * fold; hiding four of them inside the first would mean the catalogue is
 * something you find rather than something you navigate to. So: one row that
 * opens.
 *
 * The children are ordinary navigation entries and the shell filters them by
 * permission before drawing (see platform/web/frontend/app/navigation.js), so
 * somebody without `quotes:knowledge` simply does not see the learning tray.
 */
export const navigation = [
  {
    to: '/quotes',
    labelKey: 'quotes.title',
    permission: 'quotes:read',
    icon: 'file',
    group: 'business',
    children: [
      // ── El catálogo NO se esconde cuando está el almacén ──────────────────
      //
      // Lo estuvo, con un `supersededBy: 'inventory'`, y el razonamiento era
      // este: los materiales de aquí pasan a ser un espejo de la pantalla del
      // almacén, y dos entradas para una misma lista es como alguien acaba
      // corrigiendo un precio en la copia en vez de en el original.
      //
      // El razonamiento es correcto y la conclusión no, porque **este catálogo
      // no son sólo materiales**. Lleva tres clases —material, mano de obra y
      // servicio— y las dos últimas son de este módulo esté el almacén o no.
      // Lo dice el propio backend sin ambigüedad: con almacén,
      // `ownership().creatableKinds` sigue contestando `['labour', 'service']`.
      //
      // Así que esconderlo no quitaba una entrada duplicada: quitaba **el único
      // sitio donde se da de alta una hora de oficial o un servicio**, que es
      // la mitad de lo que se presupuesta. Se llegaba escribiendo la URL a
      // mano, y a eso no llega nadie.
      //
      // Lo que evita la confusión que se temía no es esconder la pantalla, es
      // lo que la pantalla ya hace: se llama «Catálogo de tarifas» y no
      // «Materiales», las filas espejadas del almacén salen de sólo lectura, el
      // botón de alta ofrece únicamente las clases que esta instancia puede
      // crear, y hay un aviso para los materiales que se dieron de alta aquí
      // antes de que llegara el almacén.
      {
        to: '/quotes/catalog',
        labelKey: 'quotes.catalog.title',
        permission: 'quotes:read',
      },
      { to: '/quotes/templates', labelKey: 'quotes.templates.title', permission: 'quotes:read' },
      { to: '/quotes/rules', labelKey: 'quotes.rules.title', permission: 'quotes:read' },
      { to: '/quotes/learning', labelKey: 'quotes.learning.title', permission: 'quotes:knowledge' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold
// to the third client. That includes what this module is called on the
// platform's own screens — Roles, Settings and Audit — so installing it does
// not leave three of them showing raw keys.
const baseMessages = {
  es: {
    errors: {
      ALREADY_DECIDED: 'Esa sugerencia ya está decidida.',
      EMPTY_QUOTE: 'Un presupuesto sin líneas no puede convertirse en plantilla.',
      INVALID_TRANSITION: 'El presupuesto no puede pasar a ese estado desde el que tiene ahora.',
      MATERIALS_OWNED_BY_INVENTORY: 'En esta instalación los materiales nuevos se dan de alta en Inventario.',
      NOTHING_TO_SEND: 'Este presupuesto no tiene ninguna línea con concepto y precio: no se puede mandar ni aceptar.',
      OWNED_BY_INVENTORY: 'Este material lo lleva Inventario. Cámbialo allí.',
      PROFILE_NAME_TAKEN: 'Ya hay un perfil de importación con ese nombre.',
      QUOTE_CHANGED: 'Otra persona ha cambiado este presupuesto mientras tanto. Recárgalo y vuelve a intentarlo.',
      QUOTE_LOCKED: 'Un presupuesto aceptado no se puede editar. Duplícalo.',
      UNKNOWN_SUGGESTION: 'Ese tipo de sugerencia no existe.',
    },
    quotes: {
      title: 'Presupuestos',
      help: {
        title: "Presupuestos",
        subtitle: "El catálogo de tarifas como base, las plantillas y las reglas como memoria, y de dónde saca los precios el asistente",
      },
      dashboard: {
        openAmount: 'Presupuestado vivo',
        openAmountHelp: 'Suma de los presupuestos en borrador y enviados, en euros: lo que está en juego.',
        sent: 'Esperando respuesta',
        sentHelp: 'Cuántos presupuestos se han enviado al cliente y todavía no tienen respuesta.',
        winRate: 'Tasa de acierto',
        winRateHelp: 'De los presupuestos que han tenido respuesta, qué porcentaje se aceptó. Los que siguen esperando no cuentan.',
        wonAmount: 'Ganado',
        wonAmountHelp: 'Suma de los presupuestos aceptados, en euros.',
        awaiting: 'Sin respuesta',
        awaitingHelp: 'Los presupuestos enviados que siguen esperando contestación, del más reciente al más antiguo. Estira la tarjeta y caben más.',
      },
      subtitle: 'Lo que ofrecéis y a qué precio',
      actions: {
        new: 'Nuevo presupuesto', edit: 'Editar', save: 'Guardar', cancel: 'Cancelar',
        remove: 'Eliminar', archive: 'Archivar', restore: 'Devolver a las listas', duplicate: 'Duplicar',
        pdf: 'Descargar PDF', send: 'Enviar al cliente', confirmSend: 'Enviar',
        markAccepted: 'Marcar aceptado', markRejected: 'Marcar rechazado',
        saveAsTemplate: 'Guardar como plantilla',
        sendAgain: 'Volver a enviar', markSent: 'Marcar como enviado',
        writeEmail: 'Escribir el correo',
      },
      status: {
        draft: 'Borrador', sent: 'Enviado', accepted: 'Aceptado',
        rejected: 'Rechazado', expired: 'Caducado', cancelled: 'Anulado',
      },
      filters: {
        oneCustomer: 'Solo de un cliente',

        search: 'Buscar', searchPlaceholder: 'Código, título o cliente…',
        status: 'Estado', allStatuses: 'Todos', sort: 'Ordenar por',
      },
      sort: { createdAt: 'Más recientes', updatedAt: 'Última modificación', total: 'Importe', validUntil: 'Validez' },
      table: {
        quote: 'Presupuesto', customer: 'Cliente', total: 'Importe', margin: 'Margen',
        validUntil: 'Válido hasta', status: 'Estado', noCustomer: 'Sin cliente',
      },
      kpi: { open: 'En curso', awaiting: 'Esperando respuesta', won: 'Ganado', winRate: 'Tasa de éxito' },
      empty: {
        none: 'Todavía no hay presupuestos',
        noneHint: 'Crea el primero. Puedes describir el trabajo y dejar que la IA prepare las líneas.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },

      detail: {
        title: 'Presupuesto',
        actions: 'Acciones',
        daysLeft: 'Caduca en {days} días',
        expiredOn: 'Caducó el {date}',
        sent: 'Presupuesto enviado',
        outcomeSaved: 'Resultado guardado',
        duplicated: 'Copia creada: {code}',
        savedAsTemplate: 'Plantilla «{name}» creada',
      },
      form: {
        subtitle: 'Describe el trabajo o arrastra del catálogo; la IA prepara las líneas',
        subtitlePhone: 'Describe el trabajo al asistente o elige del catálogo',
        sectionInfo: 'Información',
        saving: 'Guardando…', saveDraft: 'Guardar borrador', saveAndSend: 'Guardar y enviar',
        savedAsDraft: 'Guardado como borrador',
        review: 'Revisar', reviewing: 'Revisando…',
        reviewNeedsLines: 'Añade al menos una línea antes de revisar',
        linesRequired: 'Añade al menos una línea con descripción',
        badLine: 'Revisa la cantidad y el precio de «{name}»',
        newTitle: 'Nuevo presupuesto', editTitle: 'Editar presupuesto',
        newHint: 'Solo el título es obligatorio; el resto se puede completar después',
        details: 'Datos del presupuesto', notes: 'Notas',
        titleRequired: 'Falta el título',
        created: 'Presupuesto creado', saved: 'Presupuesto guardado',
      },
      fields: {
        customerPlaceholder: 'Empresa o particular', paymentTermsPlaceholder: 'Ej.: 50 % al pedido, 50 % a la entrega',
        project: 'Proyecto', projectPlaceholder: 'A qué proyecto pertenece',
        title: 'Título', titlePlaceholder: 'Ej.: Línea de envasado 2.000 botellas/h',
        customer: 'Cliente', contact: 'Persona de contacto',
        date: 'Fecha',
        validUntil: 'Válido hasta', taxRate: 'IVA', paymentTerms: 'Forma de pago',
        notes: 'Notas internas', notesHint: 'No salen en el PDF',
        notesForClient: 'Observaciones para el cliente', notesForClientHint: 'Se imprimen en el presupuesto',
      },
      lines: {
        dragHint: 'Arrastra aquí un artículo del catálogo para añadirlo como línea',
        unit: 'Ud.', subtotal: 'Subtotal', ai: 'IA', marginShort: 'Mg%',
        dragToReorder: 'Arrastrar para reordenar', select: 'Seleccionar línea',
        editMargin: 'Clic para editar el margen', noCost: 'Sin coste definido',
        manual: 'Línea manual', describe: 'Escríbela la IA', describing: 'Escribiendo…',
        annex: 'Anexo técnico de esta partida',
        annexPlaceholder: 'Qué se suministra, cómo se ejecuta y qué no entra. Sale en el anexo del PDF.',
        breakdown: 'Desglose de materiales ({count} componentes)',
        addingComponentsTo: 'Añadiendo componentes a',
        untitledLine: 'Línea sin nombre',
        componentsAdded: '{n} añadido(s) · último: {name}',
        componentsDone: 'Hecho',
        component: 'Componente / material', componentsTotal: 'Total componentes',
        title: 'Líneas', description: 'Descripción', descriptionPlaceholder: 'Concepto o conjunto',
        quantity: 'Cant.', unitPrice: 'Precio', costPrice: 'Coste', discount: 'Dto. %',
        margin: 'Margen', total: 'Total',
        add: 'Añadir línea', addComponent: 'Añadir componente', remove: 'Eliminar',
        moveUp: 'Subir', moveDown: 'Bajar',
        components: '{count} componentes', fromAi: 'IA', untitled: 'sin título',
        fromComponents: 'Se calcula sumando los componentes',
        none: 'Todavía no hay líneas',
        noneHint: 'Añade una a mano, o describe el trabajo en el panel de la derecha.',
      },
      totals: {
        globalMargin: 'Margen global', applyAll: 'Aplicar a todo',
        marginRequired: 'Escribe un porcentaje de margen antes de aplicarlo',
        noPricedLines: 'No hay líneas con precio al que aplicar el margen',
        confirmGlobalMargin: 'Se va a fijar el margen al {percent} % en {count} línea(s), sobrescribiendo sus costes. ¿Continuar?',
        title: 'Totales', subtotal: 'Base imponible', discount: 'Descuentos',
        tax: 'IVA', taxAt: 'IVA {rate} %', grandTotal: 'Total', margin: 'Margen',
      },

      ai: {
        title: 'Preparar con IA',
        intro: 'Describe el trabajo y se generan las líneas con el catálogo, las plantillas y las reglas de la empresa.',
        describe: 'Qué hay que presupuestar',
        placeholder: 'Ej.: prensa hidráulica de 100 t con cuadro eléctrico, PLC S7-1200 y puesta en marcha',
        generateQuote: 'Presupuesto completo',
        generateAssembly: 'Un conjunto',
        addToSelected: 'Añadir a «{name}»',
        proposalTitle: '{count} líneas propuestas',
        grounded: '{pct} % con precios vuestros',
        apply: 'Añadir {count} al presupuesto',
        discard: 'Descartar',
        applied: '{count} líneas añadidas',
        added: '{count} componentes añadidos',
        review: 'Revisar qué falta',
        reviewClean: 'No se ve nada que falte.',
        warning: {
          empty: 'No ha salido nada. Prueba a describir el trabajo con más detalle.',
          truncated: 'La respuesta se ha cortado por longitud. Divide la petición en partes más pequeñas.',
          unparseable: 'La respuesta no ha llegado en un formato utilizable. Vuelve a intentarlo.',
        },
      },

      preflight: {
        backAndFix: 'Volver y corregir', fixFirst: 'Corrige los errores', sendAnyway: 'Enviar igualmente',
        summaryErrors: '{errors} error(es) y {warnings} aviso(s)',
        summaryWarnings: '{warnings} aviso(s), nada que impida enviarlo',
        summaryClean: 'Todo en orden',
        groups: { info: 'Información', lines: 'Líneas', margin: 'Márgenes' },
        title: 'Antes de enviarlo',
        intro: 'Un repaso rápido. Los avisos no impiden enviarlo; los errores sí.',
        items: {
          title: 'Título del presupuesto',
          customer: 'Cliente asignado',
          validUntil: 'Fecha de validez',
          paymentTerms: 'Forma de pago',
          lines: '{count} líneas con descripción y precio',
          emptyLines: '{count} líneas sin descripción o con precio 0',
          negativeMargin: '{count} líneas con coste igual o mayor que el precio de venta',
          thinMargin: '{count} líneas por debajo del {threshold} % de margen',
          averageMargin: 'Margen medio del presupuesto: {margin} %',
          averageMarginHidden: 'Margen medio del presupuesto (la cifra solo la ve quien puede ver costes)',
          unknownCost: '{count} líneas sin coste: su margen real se desconoce',
        },
      },
      // Lo que el correo que lleva el presupuesto dice de entrada: el asunto y
      // la plantilla, en el idioma del cliente (QuotePage → composeEmail). El
      // resto lo escribe quien lo manda en el redactor de la plataforma.
      send: {
        tplSubject: {
          standard: 'Presupuesto {code} · {title}',
          followUp: 'Seguimiento del presupuesto {code}',
        },
        greeting: 'Hola {contact}:',
        greetingAnon: 'Buenos días:',
        tplBody: {
          standard: 'Te adjunto el presupuesto {code} para {title}, por un importe total de {total}.\n\nSi quieres que repasemos cualquier punto, dímelo y lo vemos.',
          followUp: 'Te recuerdo el presupuesto {code} que te mandamos, por {total}. Quedo a la espera de lo que nos digas.',
        },
        validitySentence: 'La oferta es válida hasta el {validUntil}.',
      },
      outcome: {
        title: 'Resultado',
        askTitle: 'Qué ha pasado con el presupuesto',
        askIntro: 'El motivo es opcional, pero es lo único que permite al sistema aprender de lo que se gana y se pierde.',
        reasonLabel: 'Motivo', noReason: 'Sin especificar', noteLabel: 'Comentario',
        reason: {
          price: 'Precio', scope: 'Alcance', timing: 'Plazos',
          competitor: 'Competencia', other: 'Otro',
        },
      },
      archive: {
        title: 'Archivar presupuesto',
        body: 'Vas a archivar {code}. No se borra nada: deja de aparecer en las listas.',
        done: 'Presupuesto archivado',
        restored: 'Presupuesto devuelto a las listas',
        isArchived: 'Este presupuesto está archivado: no sale en ninguna lista. Devuélvelo para volver a trabajar con él.',
      },

      pdfOptions: {
        emailTitle: 'Enviar el presupuesto por correo',
        emailIntro: 'Cómo sale el PDF que va adjunto, y en qué idioma se le escribe al cliente.',
        language: 'Idioma del cliente',
        writeEmail: 'Escribir el correo',
        languages: { es: 'Castellano', ca: 'Catalán', en: 'Inglés', fr: 'Francés' },
        title: 'Generar el PDF',
        intro: 'Elige cuánto enseña el documento. Se pregunta cada vez porque no lee lo mismo una oficina técnica que un particular.',
        generate: 'Generar el PDF',
        detail: {
          label: 'Qué enseña cada partida',
          elements: 'La partida y lo que lleva',
          elementsHelp: 'El título y, debajo, los elementos que la componen.',
          titles: 'Solo el título de la partida',
          titlesHelp: 'Ni descripción ni elementos: una línea por partida.',
        },
        prices: {
          label: 'Qué precios salen',
          groups: 'Solo el de cada partida',
          groupsHelp: 'Precio, descuento e importe por partida. Los elementos, sin precio.',
          all: 'Todos, elemento a elemento',
          allHelp: 'Cada elemento con su importe, que suma el precio unitario de la partida.',
          allNeedsElements: 'Necesita que se enseñen los elementos.',
          total: 'Solo el total (precio cerrado)',
          totalHelp: 'Las partidas sin importes. La base, el IVA y el total salen siempre.',
        },
        annex: {
          label: 'Anexo explicativo de la instalación',
          help: 'Una hoja aparte al final, partida por partida, con lo que incluye cada una.',
        },
        summary: {
          detail: { elements: 'las partidas con sus elementos', titles: 'solo los títulos de las partidas' },
          groups: 'Saldrán {detail}, con el importe de cada partida{annex}.',
          all: 'Saldrán {detail}, con el importe de cada elemento y de cada partida{annex}.',
          total: 'Saldrán {detail}, sin importes por partida: solo el total{annex}.',
          withAnnex: ', y el anexo al final',
        },
      },
      project: {
        title: 'Proyecto',
        placeholder: 'Buscar el proyecto…',
        save: 'Guardar vínculo',
        linked: 'Presupuesto vinculado al proyecto',
        unlinked: 'Presupuesto desvinculado del proyecto',
      },
      customer: {
        placeholder: 'Busca un cliente o escribe el nombre',
        searching: 'Buscando…',
        noResults: 'Ningún cliente se llama así. Se guardará el nombre tal cual.',
        linked: 'Ficha',
        unlink: 'Quitar el vínculo con la ficha del cliente',
      },
      guide: {
        catalog: {
          filters: 'Busca por nombre, referencia o proveedor. Los dos desplegables acotan por familia y por tipo: material, mano de obra o servicio.',
          table: 'Cada fila es una tarifa. El margen se calcula solo, con el precio de venta y el coste: si sale en rojo, casi siempre es un coste escrito en la casilla del precio. Pulsa una fila para corregirla.',
          form: 'El coste no sale nunca en el presupuesto del cliente: sirve para el margen y para que la IA no proponga por debajo. Si no lo sabéis, déjalo en cero. Dar de baja un artículo lo quita del catálogo sin tocar los presupuestos que ya lo usan.',
        },
        templates: {
          what: 'Una plantilla es un conjunto ya montado —componentes y horas— que entra en un presupuesto como una sola línea y arrastra su coste. No hace falta crearlas a mano: al marcar un presupuesto como aceptado se ofrece guardarlo como plantilla.',
          cards: 'Cada ficha resume lo que se mira para reutilizarla: cuántos componentes lleva, cuántas horas y con qué margen salió. Pulsa una para abrirla y ajustarla.',
        },
        rules: {
          what: 'Una regla es lo que vuestro técnico más veterano aplica sin que nadie se lo diga. Se leen antes de cada borrador y viajan con la petición a la IA: esta pantalla es donde se le enseña el oficio.',
          list: 'Cada regla se lee como se explica en voz alta: cuándo aplica, qué hay que hacer y qué pasa si se olvida. Las marcadas «Aprendida» las ha escrito el módulo al ver lo que corregís, y se editan o se borran igual que las vuestras.',
        },
        learning: {
          actions: '«Recalcular» vuelve a mirar la evidencia y rehace esta lista. «Reindexar» reconstruye la búsqueda por significado del catálogo, las plantillas y las reglas. Ninguno de los dos cambia vuestros datos.',
          metrics: 'Cuatro números para una sola pregunta: si esto va mejorando. «Sin tocar el precio» es el que la contesta: cuántos precios propuestos nadie tuvo que corregir.',
          tabs: 'Pendientes son las que esperan vuestro criterio. Aplicadas y descartadas se quedan a la vista, para saber qué se decidió y cuándo.',
          cards: 'Cada ficha dice qué ha observado, en cuántos casos —«Ver las N veces» abre la evidencia— y qué hará exactamente si la aceptáis. Nada se aplica solo: ese botón es el único momento en que el módulo escribe en vuestro catálogo o en vuestras reglas.',
        },
      },
      catalog: {
        pendingHandover: 'Quedan {count} materiales dados de alta aquí. A medida que se registren en el almacén, estas fichas se convierten solas y el aviso desaparece.',
        fromInventory: 'Del almacén',
        materialsElsewhere: 'Los materiales los lleva el módulo de almacén: aquí se ven y se pueden presupuestar, pero se dan de alta y se corrigen allí. En esta pantalla se registran la mano de obra y los servicios.',
        title: 'Catálogo de tarifas',
        subtitle: 'Lo que vendéis y lo que os cuesta',
        newIn: { rates: 'Nueva tarifa', materials: 'Nuevo material' },
        editIn: { rates: 'Editar tarifa', materials: 'Editar material' },
        formSubtitleRate: 'Qué es y qué cuesta',
        groups: {
          rates: 'Tarifas',
          ratesHint: 'Mano de obra y servicios: la hora de un oficial, la del jefe de taller, un desplazamiento.',
          materials: 'Materiales',
          materialsHint: 'Piezas y suministros que se ponen en la obra.',
        },
        category: 'Categoría', allCategories: 'Todas', kind: 'Tipo', allKinds: 'Todos',
        kinds: { material: 'Material', labour: 'Mano de obra', service: 'Servicio' },
        import: {
          open: 'Importar desde Excel',
          title: 'Importar tarifa desde Excel',
          file: 'Fichero',
          fileHint: 'Excel (.xlsx, .xls) o CSV. Hasta 8 MB.',
          profile: 'Perfil',
          profileHint: 'Cómo se lee este Excel. Se guarda una vez y sirve para la tarifa de cada año.',
          newProfile: 'Nuevo: adivinar las columnas',
          lastFile: 'Última tarifa importada con este perfil: {name}.',
          download: 'Descargar',
          empty: 'Elige el fichero de la tarifa',
          emptyText: 'Primero se enseña lo que haría. No se cambia ningún precio hasta que pulses Importar.',
          sheet: 'Hoja',
          sheetRows: '{name} ({n} filas)',
          headerRow: 'Fila de cabeceras',
          headerRowHint: '0 para buscarla sola.',
          columns: 'Qué columna es cada dato',
          notMapped: '— No está en el fichero —',
          pricing: 'Precio',
          pricingHint: 'Si la tarifa trae el PVP del proveedor: su descuento da tu coste, y tu margen da la venta. Una columna de coste o de venta manda sobre esto.',
          discount: 'Descuento del proveedor (%)',
          markup: 'Tu margen sobre el coste (%)',
          defaults: 'Para lo que el fichero no diga',
          decimal: 'Decimales',
          decimals: {
            auto: 'Detectar',
            comma: 'Coma (1.234,56)',
            dot: 'Punto (1,234.56)',
          },
          matchBy: 'Reconocer lo que ya está por',
          match: {
            reference: 'Referencia',
            name: 'Nombre',
          },
          reading: 'Leyendo el fichero…',
          count: {
            create: '{n} nuevos',
            update: '{n} cambian de precio o datos',
            unchanged: '{n} iguales',
            errors: '{n} con problemas',
            created: '{n} creados',
            updated: '{n} actualizados',
          },
          missing: 'Estas columnas del perfil no están en este fichero: {fields}. Elige otra o déjala sin asignar.',
          needsName: 'Elige qué columna es el nombre del artículo.',
          rowNumber: 'Fila',
          action: {
            create: 'Nuevo',
            update: 'Cambia',
            unchanged: 'Igual',
          },
          errorList: 'Ver las {n} filas con problemas',
          errors: {
            NO_NAME: 'Tiene precio pero no tiene nombre',
            NO_PRICE: 'No tiene precio',
            NEGATIVE_PRICE: 'Precio negativo',
            DUPLICATE_IN_FILE: 'Repetida en el fichero (vale la última)',
            OWNED_BY_INVENTORY: 'Este material es del almacén: su precio se cambia allí',
            MATERIALS_OWNED_BY_INVENTORY: 'En esta instancia los materiales se dan de alta en el almacén',
            other: 'No se pudo importar ({code})',
          },
          profileName: 'Nombre del perfil (p. ej. Tarifa Aragón)',
          saveProfile: 'Guardar perfil',
          updateProfile: 'Actualizar perfil',
          profileSaved: 'Perfil «{name}» guardado',
          run: 'Importar {n}',
          done: 'Importación terminada',
          fields: {
            reference: 'Referencia',
            name: 'Nombre',
            description: 'Descripción',
            category: 'Familia',
            unit: 'Unidad',
            kind: 'Tipo',
            costPrice: 'Coste',
            price: 'Precio de venta',
            listPrice: 'PVP del proveedor',
            supplier: 'Proveedor',
            supplierRef: 'Ref. del proveedor',
            leadTimeDays: 'Plazo (días)',
            taxRate: 'IVA (%)',
          },
        },
        searchPlaceholder: 'Nombre, referencia o proveedor…',
        leadDays: '{days} días',
        empty: 'El catálogo está vacío',
        emptyHint: 'Es el fichero que hace que un presupuesto tarde minutos en vez de una tarde.',
        emptyFiltered: 'Sin resultados', emptyFilteredHint: 'Prueba a quitar algún filtro.',
        nameRequired: 'Falta el nombre', saved: 'Artículo guardado',
        deactivate: 'Dar de baja',
        deactivateTitle: 'Dar de baja el artículo',
        deactivateBody: '«{name}» dejará de salir en el catálogo. Los presupuestos que lo usan no cambian.',
        deactivated: 'Artículo dado de baja',
        formSubtitle: "Qué es, qué cuesta y de dónde viene",
        whatItIs: "Qué es",
        whatItCosts: "Qué cuesta y a cuánto se vende",
        whereItComesFrom: "De dónde viene",
        readOnlyNote: "Esta ficha es un espejo del almacén: se lee aquí y se corrige allí. Dos sitios para el mismo precio es un sitio donde el precio está mal.",
        notFound: "Ese artículo ya no existe",
        leaveTitle: "Dejar la tarifa a medias",
        leaveBody: "Lo escrito se perderá. Una tarifa a medio rellenar no se guarda sola.",
        leaveConfirm: "Salir sin guardar",
        fields: {
          name: 'Nombre', description: 'Descripción', kind: 'Tipo', unit: 'Unidad',
          price: 'Precio de venta', costPrice: 'Coste', costHint: 'Déjalo en 0 si no lo sabéis: es mejor que un margen inventado',
          taxRate: 'IVA', reference: 'Referencia interna',
          supplier: 'Proveedor', supplierRef: 'Ref. del proveedor', leadTimeDays: 'Plazo (días)',
        },
        whoProvidesIt: 'Quién lo presta',
        whoProvidesItHint: 'Un servicio se subcontrata: quién lo hace y cuánto tarda en estar.',
        newCategory: 'Nueva familia…',
        newCategoryLabel: 'Nombre de la familia',
        otherUnit: 'Otra…',
        otherUnitLabel: 'Escribe la unidad',
        unitHint: 'Se imprime en el presupuesto, detrás del precio.',
        units: {
          hour: { value: 'h', label: 'h (hora)' },
          workday: { value: 'jornada', label: 'jornada (día de trabajo)' },
          unit: { value: 'ud.', label: 'ud. (unidad)' },
          day: { value: 'día', label: 'día' },
          km: { value: 'km', label: 'km' },
          metre: { value: 'm', label: 'm (metro)' },
          sqm: { value: 'm²', label: 'm² (metro cuadrado)' },
          cbm: { value: 'm³', label: 'm³ (metro cúbico)' },
          kg: { value: 'kg', label: 'kg (kilo)' },
          litre: { value: 'l', label: 'l (litro)' },
        },
      },

      templates: {
        edit: 'Editar plantilla', create: 'Crear plantilla', saveChanges: 'Guardar cambios',
        estimatedTotals: 'Totales estimados', globalMargin: 'Margen global',
        bom: 'Componentes BOM', addManual: '+ Manual', supplierRef: 'Proveedor · Ref',
        searchCatalog: 'Buscar catálogo', searchLabour: 'Buscar mano de obra',
        searchCatalogPlaceholder: 'Buscar pieza, componente, servicio…',
        searchLabourPlaceholder: 'Buscar puesto o especialidad…',
        searchNoResults: 'Sin resultados para «{query}»',
        searchHint: 'Escribe al menos 2 caracteres para buscar',
        noComponents: 'Sin componentes — pulsa «+ Manual» o busca en el catálogo',
        noLabour: 'Sin puestos — pulsa «+ Puesto» o busca en el catálogo',
        addRole: '+ Puesto',
        title: 'Plantillas',
        subtitle: 'Máquinas y conjuntos que ya habéis presupuestado',
        new: 'Nueva plantilla',
        formHint: 'Lo que se guarda aquí es lo que la IA usa de referencia para presupuestar algo parecido.',
        identity: 'Identificación',
        searchPlaceholder: 'Nombre o descripción…',
        components: 'Componentes', addComponent: 'Añadir componente',
        labour: 'Mano de obra', addLabour: 'Añadir puesto',
        labourHint: 'Horas y coste por hora. Lo que se cobra por hora sale del multiplicador de los ajustes.',
        hours: 'Horas', componentCost: 'Coste de materiales', labourCost: 'Coste de mano de obra',
        totalCost: 'Coste total', salePrice: 'Precio de venta',
        annex: 'Anexo técnico',
        annexHint: 'Descripción que se puede imprimir en el anexo del presupuesto.',
        annexDoc: 'Anexo de la máquina',
        annexPhotoLater: 'Guarda la plantilla para poder subirle fotos.',
        fromQuote: 'Desde presupuesto',
        empty: 'Todavía no hay plantillas',
        emptyHint: 'Al marcar un presupuesto como aceptado se te ofrecerá guardarlo como plantilla.',
        emptyFiltered: 'Sin resultados', emptyFilteredHint: 'Prueba a quitar algún filtro.',
        nameRequired: 'Falta el nombre', saved: 'Plantilla guardada', removed: 'Plantilla eliminada',
        removeTitle: 'Eliminar plantilla', removeBody: '¿Eliminar «{name}»? Los presupuestos hechos con ella no cambian.',
        fields: {
          name: 'Nombre', description: 'Descripción',
          namePlaceholder: 'Ej.: Envasadora flow pack 120 ud/min',
          tags: 'Etiquetas (separadas por comas)', tagsHint: 'Ej.: envasado, flow pack, alimentación',
          rolePlaceholder: 'Ej.: Ingeniería / Diseño',
          descriptionHint: 'Cuanto mejor la describas, mejor la encuentra la IA',
          reference: 'Referencia', component: 'Componente',
          role: 'Puesto', hours: 'Horas', rate: 'Coste/h', cost: 'Coste',
        },
      },

      rules: {
        title: 'Reglas técnicas',
        subtitle: 'Los criterios que aplicáis al presupuestar',
        new: 'Nueva regla', edit: 'Editar regla',
        formHint: 'Escríbela como se la explicarías a alguien que entra nuevo: cuándo aplica, qué hay que hacer y qué pasa si se olvida.',
        identity: "Qué regla es",
        body: "Qué dice",
        leaveTitle: "Dejar la regla a medias",
        leaveBody: "Lo escrito se perderá. Una regla a medio redactar no se guarda sola.",
        leaveConfirm: "Salir sin guardar",
        notFound: "Esa regla ya no existe",
        searchPlaceholder: 'Buscar en las reglas…',
        learned: 'Aprendida',
        empty: 'Todavía no hay reglas',
        emptyHint: 'Es lo que sabe vuestro técnico más veterano y no está escrito en ningún sitio.',
        emptyFiltered: 'Sin resultados', emptyFilteredHint: 'Prueba a quitar algún filtro.',
        required: 'Faltan el nombre o la regla', saved: 'Regla guardada', removed: 'Regla eliminada',
        removeTitle: 'Eliminar regla', removeBody: '¿Eliminar «{title}»?',
        fields: {
          title: 'Nombre', titlePlaceholder: 'Ej.: Tirada de tubería de más de 30 m',
          context: 'Cuándo aplica', contextHint: 'La situación en la que hay que tenerla en cuenta',
          rule: 'Qué hay que hacer', ruleHint: 'Qué se incluye, se añade o se comprueba',
          consequence: 'Qué pasa si se omite', example: 'Ejemplo',
        },
      },

      learning: {
        title: 'Aprendizaje',
        subtitle: 'Lo que el sistema ha observado en vuestros presupuestos, esperando vuestro criterio',
        refresh: 'Recalcular', refreshed: 'Recalculado',
        reindex: 'Reindexar', reindexed: '{count} elementos reindexados',
        noSemantic: 'La búsqueda por significado está desactivada en esta instalación: se busca por texto. Todo funciona, pero encuentra menos.',
        tabs: { pending: 'Pendientes', accepted: 'Aplicadas', dismissed: 'Descartadas' },
        metrics: {
          proposed: 'Líneas propuestas', acceptance: 'Se quedan',
          unedited: 'Sin tocar el precio', avgEdit: 'Corrección media',
        },
        kinds: {
          'price-adjust': 'Ajuste de precio',
          'new-rate-item': 'Falta en el catálogo',
          'stop-proposing': 'Dejar de proponer',
          'template-from-quote': 'Plantilla nueva',
        },
        claims: {
          'price-adjust': 'Habéis corregido «{concept}» {samples} veces: la IA propone {avgProposed} y acaba en {medianFinal} ({medianDeltaPct} %).',
          'new-rate-item': '«{name}» se escribe a mano en {samples} presupuestos y no está en el catálogo. Precio habitual: {medianPrice}.',
          'stop-proposing': '«{concept}» se ha propuesto y quitado {rejected} veces, y no se ha quedado ninguna.',
          'template-from-quote': 'El presupuesto {code} ({grandTotal}) se ganó. Puede servir de referencia para los próximos.',
        },
        effects: {
          'price-adjust': 'Actualiza el precio en el catálogo',
          'new-rate-item': 'Crea el artículo en el catálogo',
          'stop-proposing': 'Crea una regla técnica',
          'template-from-quote': 'Crea la plantilla',
        },
        evidence: 'Ver las {count} veces', rejectedTimes: 'quitada {count} veces',
        accept: 'Aplicar', dismiss: 'Descartar',
        accepted: 'Aplicado', dismissed: 'Descartado',
        empty: {
          pending: 'Nada que revisar',
          accepted: 'Todavía no habéis aplicado nada',
          dismissed: 'Nada descartado',
        },
        emptyHint: {
          pending: 'Aparecerán cosas aquí cuando el sistema vea un patrón: un precio que corregís siempre, algo que escribís a mano una y otra vez.',
          accepted: 'Lo que aceptéis se aplicará al catálogo o a las reglas y quedará listado aquí.',
          dismissed: 'Lo que descartéis no se volverá a proponer.',
        },
      },

      catalogPanel: {
        label: 'Catálogo', open: 'Abrir el catálogo', close: 'Ocultar el catálogo',
        materials: 'Materiales', rates: 'Tarifas',
        search: 'Buscar en el catálogo…', allCategories: 'Todas las categorías',
        loading: 'Cargando…', noResults: 'Sin resultados',
        dragHint: "Arrastra «{name}» a una línea, o haz clic para añadirlo",
        failed: 'No se ha podido cargar el catálogo.',
        retry: 'Reintentar',
        partial: 'Se están mirando {shown} de {total} artículos. Busca en el catálogo completo para ver el resto.',
      },

      annexAi: {
        tab: 'Asistente', title: 'Asistente del anexo', status: 'Listo', newChat: 'Empezar de nuevo',
        emptyTitle: '¿Qué tiene que decir este anexo?',
        emptyHint: 'Cuéntaselo como se lo contarías a un compañero. Escribe el documento entero o retoca el que ya hay.',
        placeholder: 'Escríbeme el anexo, o dime qué cambiar…',
        tags: { write: 'Escribir', table: 'Tabla', terms: 'Condiciones', exclusions: 'Exclusiones', tidy: 'Retocar' },
        examples: {
          write: 'Escribe el anexo de un cuadro de maniobra para cuatro motores con variador, PLC y pantalla.',
          // Con la máquina delante, el atajo la nombra a ELLA y la tabla sale de
          // sus componentes; sin máquina, los dos anexos del presupuesto hablan
          // del trabajo entero.
          writeFor: 'Escribe el anexo de «{name}»',
          tableFrom: 'Añade una tabla de características a partir de sus {n} componentes',
          intro: 'Escribe la presentación y el alcance de este presupuesto',
          terms: 'Escribe las condiciones: plazo, garantía y forma de trabajo',
          table: 'Añade una tabla con las características principales.',
          exclusions: 'Añade las exclusiones habituales de este tipo de trabajo.',
          shorter: 'Acorta el segundo párrafo y quita la palabrería.',
        },
        proposal: 'Propone {n} bloque(s)', apply: 'Aplicar', discard: 'Descartar', applied: 'Aplicado al documento.',
        review: {
          title: 'Propone {n} bloque(s). Repásalos:',
          all: 'Marcar todos', none: 'Desmarcar todos',
          apply: 'Aplicar {n} al documento',
          applied: '{n} bloque(s) aplicados al documento.',
          help: 'Lo marcado será el documento. Lo que desmarques no se queda, y después puedes retocarlo todo en la columna del documento.',
          state: { new: 'Nuevo', changed: 'Cambiado', same: 'Igual' },
        },
        done: 'Listo.',
        // Un turno que no produjo nada dice por qué, y cada causa lleva a un
        // sitio distinto. «Vuelve a intentarlo» para las tres manda a la gente
        // a insistir en lo que no puede salir.
        warnings: {
          truncated: 'Se ha cortado a mitad: lo que has pedido no cabe de una vez. Pídelo por partes —primero lo que se suministra, luego las características— o acorta la instrucción.',
          unparseable: 'La respuesta ha llegado rota y no he podido usarla. Vuelve a intentarlo.',
          empty: 'No ha salido nada. Cuéntamelo de otra manera.',
        },
      },
      annexDoc: {
        title: 'Anexo técnico',
        intro: 'Presentación y alcance',
        closing: 'Condiciones y exclusiones',
        saveFirst: 'Guarda el presupuesto para poder subirle fotos.',
      },

      annex: {
        title: 'Anexo técnico',
        edit: 'Editar', write: 'Escribir anexo',
        blocks: '{n} bloque(s)',
        emptyCta: 'Sin anexo todavía. Pulsa para escribirlo.',
        writing: 'Documento', resize: 'Arrastra para repartir; doble clic lo devuelve',
        asPrinted: 'Como saldrá impreso',
        panels: { hide: 'Cerrar «{name}»', show: 'Abrir «{name}»', reopen: 'Cerradas' },
        rendering: 'Dibujando…', emptyPreview: 'Nada que enseñar todavía.',
        vars: {
          title: 'Datos que se rellenan',
          hint: {
            template: 'El mismo anexo sirve para la máquina de 15 kW y para la de 400. Escribe la clave entre llaves dobles dentro del texto y declárala aquí.',
            quote: 'Los huecos que pide este anexo. Lo que dejes vacío se imprime con las llaves puestas.',
          },
          add: '+ Hueco', key: 'clave', label: 'Cómo se lee', unit: 'Unidad',
          none: 'Este anexo no pide ningún dato.',
          loose: 'El texto usa huecos sin declarar:',
          looseAnswer: 'Huecos escritos en el texto. Contéstalos aquí; lo que quede vacío sale con las llaves puestas.',
        },
        auto: {
          title: 'Datos del presupuesto que se rellenan solos',
          hint: 'Pulsa uno para copiarlo y pégalo en el texto. Al imprimir se cambia por el dato de este presupuesto.',
          copied: 'Copiado {hole}. Pégalo en el texto.',
          groups: { quote: 'Presupuesto', customer: 'Cliente', company: 'Tu empresa' },
          keys: {
            quote_code: 'Código',
            quote_title: 'Título',
            quote_date: 'Fecha',
            quote_validUntil: 'Válido hasta',
            quote_subtotal: 'Base imponible',
            quote_total: 'Total',
            quote_paymentTerms: 'Forma de pago',
            customer_name: 'Nombre',
            customer_contact: 'Persona de contacto',
            company_name: 'Nombre',
            company_taxId: 'NIF',
            company_phone: 'Teléfono',
            company_email: 'Correo',
            today: 'Fecha de hoy',
          },
        },
        types: { heading: 'Título', paragraph: 'Párrafo', list: 'Lista', table: 'Tabla', image: 'Foto' },
        up: 'Subir', down: 'Bajar', remove: 'Quitar',
        empty: 'El anexo está vacío. Añade un bloque para empezar.',
        headingPlaceholder: 'Alcance del suministro',
        paragraphPlaceholder: 'Qué se suministra, cómo se ejecuta y qué no entra.',
        itemPlaceholder: 'Una condición, una exclusión…', addItem: '+ Línea',
        header: 'Columna', addRow: '+ Fila', addColumn: '+ Columna', removeColumn: '− Columna',
        noPhoto: 'Sin foto', upload: 'Subir foto', uploading: 'Subiendo…', choose: 'Elegir una ya subida',
        captionPlaceholder: 'Pie de foto',
      },
      composer: {
        resize: 'Arrastra para cambiar el ancho; doble clic para restaurarlo',
        tabs: 'Qué se está viendo',
        tab: { quote: 'Presupuesto', catalog: 'Catálogo', assistant: 'Asistente' },
        replies: {
          done: 'Listo: {n} línea(s) preparadas. Marca las que quieras y añádelas.',
          empty: 'No he podido preparar nada. Prueba a describirlo con algo más de detalle.',
          truncated: 'Se ha cortado a media respuesta: el trabajo es largo. Pídemelo por partes o descríbelo más corto.',
          retry: 'No he sabido darte una respuesta utilizable. Vuelve a intentarlo, y si insiste, descríbelo de otra manera.',
          'no-credit': 'La cuenta de IA de esta instancia se ha quedado sin saldo, así que no puedo redactar nada. Avisa a quien la lleve: hasta que la recarguen, reintentarlo no va a funcionar.',
          'no-target': 'No encuentro esa línea en el presupuesto. Dime cómo se llama exactamente.',
          'no-selection': 'Selecciona antes una línea con el punto de su izquierda.',
          'empty-quote': 'El presupuesto está vacío: no hay nada de lo que hacer una variante.',
          idle: '¿En qué te ayudo con este presupuesto?',
        },
        label: 'Asistente', open: 'Abrir el asistente', collapse: 'Ocultar',
        expand: 'Ampliar', restore: 'Restaurar',
        title: 'Asistente de presupuestos', status: 'Listo',
        newChat: 'Nueva conversación',
        emptyTitle: '¿Qué hay que presupuestar?',
        emptyHint: 'Descríbelo como se lo contarías a un compañero. Las líneas se generan con vuestro catálogo, vuestras plantillas y vuestras reglas.',
        placeholder: 'Describe la máquina, el conjunto o lo que hay que añadir…',
        selected: 'Seleccionada: <strong>{name}</strong>',
        selectedHint: '— dile qué hacer con ella',
        selectedOf: '{n} de {total} seleccionadas',
        all: 'Todo', none: 'Nada',
        include: 'Incluir esta línea',
        parts: '{n} componentes',
        fromTemplate: 'Encontrado en vuestras plantillas',
        willAddTo: "Se añadirá dentro de «{name}»",
        addToQuote: 'Añadir al presupuesto',
        replaceVariant: 'Reemplazar por esta variante',
        added: 'Añadido al presupuesto',
        thinking: {
          start: 'Pensando…', reading: 'Analizando tu petición…',
          catalogue: 'Buscando en el catálogo…', knowledge: 'Consultando plantillas y reglas…',
          pricing: 'Calculando precios y márgenes…', writing: 'Preparando la propuesta…',
        },
        examples: {
          wholeLabel: 'Máquina',
          whole: 'Genera el presupuesto de una prensa hidráulica de 150 t con PLC y panel HMI',
          templateLabel: 'Plantilla',
          template: 'Añade la máquina estándar que solemos hacer',
          assemblyLabel: 'Conjunto',
          assembly: 'Cuadro eléctrico: PLC, 6 bornas de 2,5 mm², carril DIN y canaleta',
          addLabel: 'Añadir',
          add: 'Añade las partidas de proyecto técnico, legalización y puesta en marcha',
          // Los cuatro de arriba son el recambio: sólo salen cuando la casa
          // todavía no tiene catálogo ni plantillas. Con datos, se escriben con
          // los nombres de verdad, que es lo que hace que la búsqueda los
          // encuentre y el precio salga anclado en vez de estimado.
          wholeReal: 'Presupuesta «{name}»',
          templateReal: 'Añade «{name}» al presupuesto',
          assemblyReal: 'Monta un conjunto con {items}',
          addReal: 'Añade {items}',
        },
      },
      review: {
        title: 'Revisión técnica', subtitle: 'Qué puede faltar antes de enviarlo',
        working: 'Analizando el presupuesto…',
        clean: 'El presupuesto parece completo: no se ve nada que falte.',
        addSuggested: "Añadir «{name}»",
      },
      search: { subtitle: 'Abrir el presupuesto' },
      // Lo poco que el PDF dice y no está ya en otra pantalla. Va aquí porque
      // el backend no tiene i18n: la ficha manda estas palabras con la
      // petición, para que el documento y la pantalla digan lo mismo.
      pdf: { taxId: 'CIF' },
      // Los avisos que este módulo aporta. Cada uno es un interruptor propio en
      // las preferencias, así que se pueden silenciar por separado.
      notifications: {
        categories: {
          expired: 'Un presupuesto ha caducado',
          accepted: 'Un presupuesto ha sido aceptado',
        },
      },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ────────
    modules: { quotes: 'Presupuestos' },
    assistant: {
      openers: {
        quotes: {
          'how-are-quotes-going': '¿Cómo van los presupuestos este mes?',
          'waiting-for-an-answer': '¿Qué presupuestos están esperando respuesta?',
          'draft-a-quote': 'Prepárame un presupuesto de lo que te voy a describir',
        },
      },
    },
    permissions: {
      quotes: {
        read: 'Ver presupuestos, catálogo y plantillas',
        write: 'Crear y editar presupuestos, y usar la IA',
        costs: 'Ver y cambiar costes y márgenes',
        send: 'Enviar un presupuesto al cliente',
        catalog: 'Cambiar precios del catálogo',
        knowledge: 'Editar plantillas, reglas y aprendizaje',
        import: 'Importar la tarifa desde un Excel',
      },
    },
    settings: {
      ns: {
        quotes: {
          title: 'Presupuestos',
          subtitle: 'Cómo presupuesta esta empresa',
          acceptFromPortal: {
            label: 'Que el cliente acepte desde su portal cierra el presupuesto',
            help: 'Su respuesta cambia el estado y lo anuncia, igual que si lo hubiera pulsado alguien de aquí. Solo hace algo con el portal de cliente instalado.',
          },
          trade: {
            label: 'Qué fabrica o instala esta empresa',
            help: 'Dos o tres líneas con vuestras palabras. Es lo que hace que la IA sepa de qué habláis.',
          },
          defaultTaxRate: { label: 'IVA con el que nace un presupuesto' },
          defaultCurrency: { label: 'Moneda de los presupuestos', help: 'ISO 4217, tres letras.' },
          defaultValidityDays: {
            label: 'Días de validez de un presupuesto',
            help: 'Pasada la fecha se marca caducado solo. Ponlo a 0 para que no caduquen.',
          },
          defaultPaymentTerms: { label: 'Forma de pago por defecto' },
          minMarginPct: {
            label: 'Margen por debajo del cual se avisa antes de enviar',
            help: 'Solo avisa: nunca impide enviar nada, se asegura de que lo habéis visto.',
          },
          labourMarkup: {
            label: 'Multiplicador del coste de una hora a lo que se cobra',
            help: '1,5 significa que una hora que cuesta 40 se cobra a 60.',
          },
        },
      },
    },
    audit: {
      actions: {
        quotes: {
          quote_created: 'Presupuesto creado',

          quote_draft: 'Presupuesto devuelto a borrador',
          quote_updated: 'Presupuesto modificado',
          quote_sent: 'Presupuesto enviado',
          quote_emailed: 'Presupuesto mandado por correo',
          quote_accepted: 'Presupuesto aceptado',
          quote_linked_to_project: 'Presupuesto vinculado a un proyecto',
          quote_rejected: 'Presupuesto rechazado',
          quote_expired: 'Presupuesto caducado',
          quote_cancelled: 'Presupuesto anulado',
          quote_duplicated: 'Presupuesto duplicado',
          quote_archived: 'Presupuesto archivado',
          quote_restored: 'Presupuesto devuelto a las listas',
          rate_item_created: 'Artículo añadido al catálogo',
          rate_item_updated: 'Artículo del catálogo modificado',
          rate_item_deactivated: 'Artículo dado de baja',
          rate_items_imported: 'Tarifa importada desde un fichero',
          import_profile_created: 'Perfil de importación creado',
          import_profile_updated: 'Perfil de importación modificado',
          import_profile_deleted: 'Perfil de importación eliminado',
          template_created: 'Plantilla creada',
          template_updated: 'Plantilla modificada',
          template_deleted: 'Plantilla eliminada',
          rule_created: 'Regla técnica creada',
          rule_updated: 'Regla técnica modificada',
          rule_deleted: 'Regla técnica eliminada',
          suggestion_accepted: 'Sugerencia de aprendizaje aplicada',
          suggestion_dismissed: 'Sugerencia de aprendizaje descartada',
        },
      },
    },
  },

  ca: {
    errors: {
      ALREADY_DECIDED: 'Aquest suggeriment ja està decidit.',
      EMPTY_QUOTE: 'Un pressupost sense línies no es pot convertir en plantilla.',
      INVALID_TRANSITION: 'El pressupost no pot passar a aquest estat des del que té ara.',
      MATERIALS_OWNED_BY_INVENTORY: 'En aquesta instal·lació els materials nous es donen d’alta a Inventari.',
      NOTHING_TO_SEND: 'Aquest pressupost no té cap línia amb concepte i preu: no es pot enviar ni acceptar.',
      OWNED_BY_INVENTORY: 'Aquest material el porta Inventari. Canvia’l allà.',
      PROFILE_NAME_TAKEN: 'Ja hi ha un perfil d’importació amb aquest nom.',
      QUOTE_CHANGED: 'Una altra persona ha canviat aquest pressupost mentrestant. Torna’l a carregar i torna-ho a provar.',
      QUOTE_LOCKED: 'Un pressupost acceptat no es pot editar. Duplica’l.',
      UNKNOWN_SUGGESTION: 'Aquest tipus de suggeriment no existeix.',
    },
    quotes: {
      title: 'Pressupostos',
      help: {
        title: "Pressupostos",
        subtitle: "El catàleg de tarifes com a base, les plantilles i les regles com a memòria, i d’on treu els preus l’assistent",
      },
      dashboard: {
        openAmount: 'Pressupostat viu',
        openAmountHelp: 'Suma dels pressupostos en esborrany i enviats, en euros: el que està en joc.',
        sent: 'Esperant resposta',
        sentHelp: 'Quants pressupostos s\'han enviat al client i encara no tenen resposta.',
        winRate: 'Taxa d\'encert',
        winRateHelp: 'Dels pressupostos que han tingut resposta, quin percentatge es va acceptar. Els que segueixen esperant no compten.',
        wonAmount: 'Guanyat',
        wonAmountHelp: 'Suma dels pressupostos acceptats, en euros.',
        awaiting: 'Sense resposta',
        awaitingHelp: 'Els pressupostos enviats que segueixen esperant contestació, del més recent al més antic. Estira la targeta i n\'hi caben més.',
      },
      subtitle: 'El que oferiu i a quin preu',
      actions: {
        new: 'Nou pressupost', edit: 'Editar', save: 'Desar', cancel: 'Cancel·lar',
        remove: 'Eliminar', archive: 'Arxivar', restore: 'Retornar a les llistes', duplicate: 'Duplicar',
        pdf: 'Descarregar PDF', send: 'Enviar al client', confirmSend: 'Enviar',
        markAccepted: 'Marcar acceptat', markRejected: 'Marcar rebutjat',
        saveAsTemplate: 'Desar com a plantilla',
        sendAgain: 'Tornar a enviar', markSent: 'Marcar com a enviat',
        writeEmail: 'Escriure el correu',
      },
      status: {
        draft: 'Esborrany', sent: 'Enviat', accepted: 'Acceptat',
        rejected: 'Rebutjat', expired: 'Caducat', cancelled: 'Anul·lat',
      },
      filters: {
        oneCustomer: "Només d'un client",

        search: 'Cercar', searchPlaceholder: 'Codi, títol o client…',
        status: 'Estat', allStatuses: 'Tots', sort: 'Ordenar per',
      },
      sort: { createdAt: 'Més recents', updatedAt: 'Última modificació', total: 'Import', validUntil: 'Validesa' },
      table: {
        quote: 'Pressupost', customer: 'Client', total: 'Import', margin: 'Marge',
        validUntil: 'Vàlid fins', status: 'Estat', noCustomer: 'Sense client',
      },
      kpi: { open: 'En curs', awaiting: 'Esperant resposta', won: 'Guanyat', winRate: "Taxa d'èxit" },
      empty: {
        none: 'Encara no hi ha pressupostos',
        noneHint: 'Crea el primer. Pots descriure la feina i deixar que la IA prepari les línies.',
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },

      detail: {
        title: 'Pressupost',
        actions: 'Accions',
        daysLeft: 'Caduca en {days} dies',
        expiredOn: 'Va caducar el {date}',
        sent: 'Pressupost enviat',
        outcomeSaved: 'Resultat desat',
        duplicated: 'Còpia creada: {code}',
        savedAsTemplate: 'Plantilla «{name}» creada',
      },
      form: {
        subtitle: 'Descriu la feina o arrossega del catàleg; la IA prepara les línies',
        subtitlePhone: "Descriu la feina a l'assistent o tria del catàleg",
        sectionInfo: 'Informació',
        saving: 'Desant…', saveDraft: 'Desar esborrany', saveAndSend: 'Desar i enviar',
        savedAsDraft: 'Desat com a esborrany',
        review: 'Revisar', reviewing: 'Revisant…',
        reviewNeedsLines: 'Afegeix almenys una línia abans de revisar',
        linesRequired: 'Afegeix almenys una línia amb descripció',
        badLine: 'Revisa la quantitat i el preu de «{name}»',
        newTitle: 'Nou pressupost', editTitle: 'Editar pressupost',
        newHint: 'Només cal el títol; la resta es pot completar després',
        details: 'Dades del pressupost', notes: 'Notes',
        titleRequired: 'Falta el títol',
        created: 'Pressupost creat', saved: 'Pressupost desat',
      },
      fields: {
        customerPlaceholder: 'Empresa o particular', paymentTermsPlaceholder: 'Ex.: 50 % a la comanda, 50 % al lliurament',
        project: 'Projecte', projectPlaceholder: 'A quin projecte pertany',
        title: 'Títol', titlePlaceholder: "Ex.: Línia d'envasat 2.000 ampolles/h",
        customer: 'Client', contact: 'Persona de contacte',
        date: 'Data',
        validUntil: 'Vàlid fins', taxRate: 'IVA', paymentTerms: 'Forma de pagament',
        notes: 'Notes internes', notesHint: 'No surten al PDF',
        notesForClient: 'Observacions per al client', notesForClientHint: "S'imprimeixen al pressupost",
      },
      lines: {
        dragHint: "Arrossega aquí un article del catàleg per afegir-lo com a línia",
        unit: 'Ud.', subtotal: 'Subtotal', ai: 'IA', marginShort: 'Mg%',
        dragToReorder: 'Arrossegar per reordenar', select: 'Seleccionar línia',
        editMargin: 'Clic per editar el marge', noCost: 'Sense cost definit',
        manual: 'Línia manual', describe: 'Que l’escrigui la IA', describing: 'Escrivint…',
        annex: 'Annex tècnic d’aquesta partida',
        annexPlaceholder: 'Què se subministra, com s’executa i què no hi entra. Surt a l’annex del PDF.',
        breakdown: 'Desglossament de materials ({count} components)',
        addingComponentsTo: 'Afegint components a',
        untitledLine: 'Línia sense nom',
        componentsAdded: '{n} afegit(s) · darrer: {name}',
        componentsDone: 'Fet',
        component: 'Component / material', componentsTotal: 'Total components',
        title: 'Línies', description: 'Descripció', descriptionPlaceholder: 'Concepte o conjunt',
        quantity: 'Quant.', unitPrice: 'Preu', costPrice: 'Cost', discount: 'Dte. %',
        margin: 'Marge', total: 'Total',
        add: 'Afegir línia', addComponent: 'Afegir component', remove: 'Eliminar',
        moveUp: 'Pujar', moveDown: 'Baixar',
        components: '{count} components', fromAi: 'IA', untitled: 'sense títol',
        fromComponents: 'Es calcula sumant els components',
        none: 'Encara no hi ha línies',
        noneHint: "Afegeix-ne una a mà, o descriu la feina al panell de la dreta.",
      },
      totals: {
        globalMargin: 'Marge global', applyAll: 'Aplicar a tot',
        marginRequired: "Escriu un percentatge de marge abans d'aplicar-lo",
        noPricedLines: 'No hi ha línies amb preu al qual aplicar el marge',
        confirmGlobalMargin: 'Es fixarà el marge al {percent} % en {count} línia/es, sobreescrivint-ne els costos. Continuar?',
        title: 'Totals', subtotal: 'Base imposable', discount: 'Descomptes',
        tax: 'IVA', taxAt: 'IVA {rate} %', grandTotal: 'Total', margin: 'Marge',
      },

      ai: {
        title: 'Preparar amb IA',
        intro: "Descriu la feina i es generen les línies amb el catàleg, les plantilles i les regles de l'empresa.",
        describe: 'Què cal pressupostar',
        placeholder: 'Ex.: premsa hidràulica de 100 t amb quadre elèctric, PLC S7-1200 i posada en marxa',
        generateQuote: 'Pressupost complet',
        generateAssembly: 'Un conjunt',
        addToSelected: 'Afegir a «{name}»',
        proposalTitle: '{count} línies proposades',
        grounded: '{pct} % amb preus vostres',
        apply: 'Afegir-ne {count} al pressupost',
        discard: 'Descartar',
        applied: '{count} línies afegides',
        added: '{count} components afegits',
        review: 'Revisar què falta',
        reviewClean: 'No es veu que falti res.',
        warning: {
          empty: 'No ha sortit res. Prova de descriure la feina amb més detall.',
          truncated: 'La resposta s\'ha tallat per llargada. Divideix la petició en parts més petites.',
          unparseable: 'La resposta no ha arribat en un format utilitzable. Torna-ho a provar.',
        },
      },

      preflight: {
        backAndFix: 'Tornar i corregir', fixFirst: 'Corregeix els errors', sendAnyway: 'Enviar igualment',
        summaryErrors: '{errors} error(s) i {warnings} avís(os)',
        summaryWarnings: "{warnings} avís(os), res que impedeixi enviar-lo",
        summaryClean: 'Tot en ordre',
        groups: { info: 'Informació', lines: 'Línies', margin: 'Marges' },
        title: "Abans d'enviar-lo",
        intro: 'Un repàs ràpid. Els avisos no impedeixen enviar-lo; els errors sí.',
        items: {
          title: 'Títol del pressupost',
          customer: 'Client assignat',
          validUntil: 'Data de validesa',
          paymentTerms: 'Forma de pagament',
          lines: '{count} línies amb descripció i preu',
          emptyLines: '{count} línies sense descripció o amb preu 0',
          negativeMargin: '{count} línies amb cost igual o superior al preu de venda',
          thinMargin: '{count} línies per sota del {threshold} % de marge',
          averageMargin: 'Marge mitjà del pressupost: {margin} %',
          averageMarginHidden: 'Marge mitjà del pressupost (la xifra només la veu qui pot veure costos)',
          unknownCost: '{count} línies sense cost: el marge real es desconeix',
        },
      },
      // Lo que el correo que lleva el presupuesto dice de entrada: el asunto y
      // la plantilla, en el idioma del cliente (QuotePage → composeEmail). El
      // resto lo escribe quien lo manda en el redactor de la plataforma.
      send: {
        tplSubject: {
          standard: 'Pressupost {code} · {title}',
          followUp: 'Seguiment del pressupost {code}',
        },
        greeting: 'Hola {contact}:',
        greetingAnon: 'Bon dia:',
        tplBody: {
          standard: "T'adjunto el pressupost {code} per a {title}, per un import total de {total}.\n\nSi vols que repassem qualsevol punt, digue-m'ho i ho veiem.",
          followUp: "Et recordo el pressupost {code} que et vam enviar, per {total}. Quedo a l'espera del que ens diguis.",
        },
        validitySentence: "L'oferta és vàlida fins al {validUntil}.",
      },
      outcome: {
        title: 'Resultat',
        askTitle: 'Què ha passat amb el pressupost',
        askIntro: "El motiu és opcional, però és l'única cosa que permet al sistema aprendre del que es guanya i es perd.",
        reasonLabel: 'Motiu', noReason: 'Sense especificar', noteLabel: 'Comentari',
        reason: {
          price: 'Preu', scope: 'Abast', timing: 'Terminis',
          competitor: 'Competència', other: 'Altres',
        },
      },
      archive: {
        title: 'Arxivar pressupost',
        body: "Vas a arxivar {code}. No s'esborra res: deixa d'aparèixer a les llistes.",
        done: 'Pressupost arxivat',
        restored: 'Pressupost retornat a les llistes',
        isArchived: 'Aquest pressupost està arxivat: no surt a cap llista. Retorna\'l per tornar-hi a treballar.',
      },

      pdfOptions: {
        emailTitle: 'Enviar el pressupost per correu',
        emailIntro: 'Com surt el PDF adjunt, i en quin idioma s\'escriu al client.',
        language: 'Idioma del client',
        writeEmail: 'Escriure el correu',
        languages: { es: 'Castellà', ca: 'Català', en: 'Anglès', fr: 'Francès' },
        title: 'Generar el PDF',
        intro: 'Tria quant ensenya el document. Es pregunta cada vegada perquè no llegeix el mateix una oficina tècnica que un particular.',
        generate: 'Generar el PDF',
        detail: {
          label: 'Què ensenya cada partida',
          elements: 'La partida i el que porta',
          elementsHelp: 'El títol i, a sota, els elements que la componen.',
          titles: 'Només el títol de la partida',
          titlesHelp: 'Ni descripció ni elements: una línia per partida.',
        },
        prices: {
          label: 'Quins preus surten',
          groups: 'Només el de cada partida',
          groupsHelp: 'Preu, descompte i import per partida. Els elements, sense preu.',
          all: 'Tots, element a element',
          allHelp: 'Cada element amb el seu import, que suma el preu unitari de la partida.',
          allNeedsElements: "Cal que s'ensenyin els elements.",
          total: 'Només el total (preu tancat)',
          totalHelp: 'Les partides sense imports. La base, l\'IVA i el total surten sempre.',
        },
        annex: {
          label: 'Annex explicatiu de la instal·lació',
          help: 'Un full a part al final, partida per partida, amb el que inclou cadascuna.',
        },
        summary: {
          detail: { elements: 'les partides amb els seus elements', titles: 'només els títols de les partides' },
          groups: "Sortiran {detail}, amb l'import de cada partida{annex}.",
          all: "Sortiran {detail}, amb l'import de cada element i de cada partida{annex}.",
          total: 'Sortiran {detail}, sense imports per partida: només el total{annex}.',
          withAnnex: ", i l'annex al final",
        },
      },
      project: {
        title: 'Projecte',
        placeholder: 'Cercar el projecte…',
        save: 'Desar el vincle',
        linked: 'Pressupost vinculat al projecte',
        unlinked: 'Pressupost desvinculat del projecte',
      },
      customer: {
        placeholder: 'Cerca un client o escriu el nom',
        searching: 'Cercant…',
        noResults: 'Cap client es diu així. Es desarà el nom tal com està.',
        linked: 'Fitxa',
        unlink: 'Treure el vincle amb la fitxa del client',
      },
      guide: {
        catalog: {
          filters: "Cerca per nom, referència o proveïdor. Els dos desplegables limiten per família i per tipus: material, mà d'obra o servei.",
          table: 'El marge surt sol, del preu de venda i del cost: si apareix en roig, gairebé sempre és un cost escrit a la casella del preu. Prem una fila per corregir-la.',
          form: "El cost no surt mai al pressupost del client: serveix per al marge i perquè la IA no proposi per sota. Si no el sabeu, deixeu-lo a zero. Donar de baixa un article el treu del catàleg sense tocar els pressupostos que ja el fan servir.",
        },
        templates: {
          what: 'Una plantilla és un conjunt ja muntat —components i hores— que entra en un pressupost com una sola línia i arrossega el seu cost. No cal crear-les a mà: en marcar un pressupost com a acceptat se us ofereix desar-lo com a plantilla.',
          cards: 'Cada fitxa resumeix el que es mira per reutilitzar-la: quants components porta, quantes hores i amb quin marge va sortir. Prem-ne una per obrir-la i ajustar-la.',
        },
        rules: {
          what: "Una regla és el que el vostre tècnic més veterà aplica sense que ningú l'hi digui. Es llegeixen abans de cada esborrany i viatgen amb la petició a la IA: aquesta pantalla és on se li ensenya l'ofici.",
          list: "Cada regla es llegeix com s'explica en veu alta: quan aplica, què s'ha de fer i què passa si s'oblida. Les marcades «Apresa» les ha escrit el mòdul veient el que corregiu, i s'editen o s'esborren igual que les vostres.",
        },
        learning: {
          actions: "«Recalcular» torna a mirar l'evidència i refà aquesta llista. «Reindexar» reconstrueix la cerca per significat del catàleg, les plantilles i les regles. Cap dels dos canvia les vostres dades.",
          metrics: 'Quatre números per a una sola pregunta: si això va millorant. «Sense tocar el preu» és el que la contesta: quants preus proposats ningú va haver de corregir.',
          tabs: 'Pendents són les que esperen el vostre criteri. Aplicades i descartades es queden a la vista, per saber què es va decidir i quan.',
          cards: "Cada fitxa diu què ha observat, en quants casos —«Veure les N vegades» obre l'evidència— i què farà exactament si l'accepteu. Res s'aplica sol: aquest botó és l'únic moment en què el mòdul escriu al vostre catàleg o a les vostres regles.",
        },
      },
      catalog: {
        pendingHandover: "Queden {count} materials donats d'alta aquí. A mesura que es registrin al magatzem, aquestes fitxes es converteixen soles i l'avís desapareix.",
        fromInventory: 'Del magatzem',
        materialsElsewhere: "Els materials els porta el mòdul de magatzem: aquí es veuen i es poden pressupostar, però es donen d'alta i es corregeixen allà. En aquesta pantalla es registren la mà d'obra i els serveis.",
        title: 'Catàleg de tarifes',
        subtitle: 'El que veneu i el que us costa',
        newIn: { rates: 'Nova tarifa', materials: 'Nou material' },
        editIn: { rates: 'Editar tarifa', materials: 'Editar material' },
        formSubtitleRate: 'Què és i què costa',
        groups: {
          rates: 'Tarifes',
          ratesHint: "Mà d'obra i serveis: l'hora d'un oficial, la del cap de taller, un desplaçament.",
          materials: 'Materials',
          materialsHint: "Peces i subministraments que es posen a l'obra.",
        },
        category: 'Categoria', allCategories: 'Totes', kind: 'Tipus', allKinds: 'Tots',
        kinds: { material: 'Material', labour: "Mà d'obra", service: 'Servei' },
        import: {
          open: 'Importar des d\'Excel',
          title: 'Importar tarifa des d\'Excel',
          file: 'Fitxer',
          fileHint: 'Excel (.xlsx, .xls) o CSV. Fins a 8 MB.',
          profile: 'Perfil',
          profileHint: 'Com es llegeix aquest Excel. Es desa una vegada i serveix per a la tarifa de cada any.',
          newProfile: 'Nou: endevinar les columnes',
          lastFile: 'Última tarifa importada amb aquest perfil: {name}.',
          download: 'Descarregar',
          empty: 'Tria el fitxer de la tarifa',
          emptyText: 'Primer s\'ensenya el que faria. No es canvia cap preu fins que premis Importar.',
          sheet: 'Full',
          sheetRows: '{name} ({n} files)',
          headerRow: 'Fila de capçaleres',
          headerRowHint: '0 per buscar-la sola.',
          columns: 'Quina columna és cada dada',
          notMapped: '— No és al fitxer —',
          pricing: 'Preu',
          pricingHint: 'Si la tarifa porta el PVP del proveïdor: el seu descompte dona el teu cost, i el teu marge dona la venda. Una columna de cost o de venda mana sobre això.',
          discount: 'Descompte del proveïdor (%)',
          markup: 'El teu marge sobre el cost (%)',
          defaults: 'Per al que el fitxer no digui',
          decimal: 'Decimals',
          decimals: {
            auto: 'Detectar',
            comma: 'Coma (1.234,56)',
            dot: 'Punt (1,234.56)',
          },
          matchBy: 'Reconèixer el que ja hi és per',
          match: {
            reference: 'Referència',
            name: 'Nom',
          },
          reading: 'Llegint el fitxer…',
          count: {
            create: '{n} nous',
            update: '{n} canvien de preu o dades',
            unchanged: '{n} iguals',
            errors: '{n} amb problemes',
            created: '{n} creats',
            updated: '{n} actualitzats',
          },
          missing: 'Aquestes columnes del perfil no són en aquest fitxer: {fields}. Tria\'n una altra o deixa-la sense assignar.',
          needsName: 'Tria quina columna és el nom de l\'article.',
          rowNumber: 'Fila',
          action: {
            create: 'Nou',
            update: 'Canvia',
            unchanged: 'Igual',
          },
          errorList: 'Veure les {n} files amb problemes',
          errors: {
            NO_NAME: 'Té preu però no té nom',
            NO_PRICE: 'No té preu',
            NEGATIVE_PRICE: 'Preu negatiu',
            DUPLICATE_IN_FILE: 'Repetida al fitxer (val l\'última)',
            OWNED_BY_INVENTORY: 'Aquest material és del magatzem: el seu preu es canvia allà',
            MATERIALS_OWNED_BY_INVENTORY: 'En aquesta instància els materials es donen d\'alta al magatzem',
            other: 'No s\'ha pogut importar ({code})',
          },
          profileName: 'Nom del perfil (p. ex. Tarifa Aragó)',
          saveProfile: 'Desar perfil',
          updateProfile: 'Actualitzar perfil',
          profileSaved: 'Perfil «{name}» desat',
          run: 'Importar {n}',
          done: 'Importació acabada',
          fields: {
            reference: 'Referència',
            name: 'Nom',
            description: 'Descripció',
            category: 'Família',
            unit: 'Unitat',
            kind: 'Tipus',
            costPrice: 'Cost',
            price: 'Preu de venda',
            listPrice: 'PVP del proveïdor',
            supplier: 'Proveïdor',
            supplierRef: 'Ref. del proveïdor',
            leadTimeDays: 'Termini (dies)',
            taxRate: 'IVA (%)',
          },
        },
        searchPlaceholder: 'Nom, referència o proveïdor…',
        leadDays: '{days} dies',
        empty: 'El catàleg és buit',
        emptyHint: "És el fitxer que fa que un pressupost trigui minuts en comptes d'una tarda.",
        emptyFiltered: 'Sense resultats', emptyFilteredHint: 'Prova de treure algun filtre.',
        nameRequired: 'Falta el nom', saved: 'Article desat',
        deactivate: 'Donar de baixa',
        deactivateTitle: "Donar de baixa l'article",
        deactivateBody: '«{name}» deixarà de sortir al catàleg. Els pressupostos que el fan servir no canvien.',
        deactivated: 'Article donat de baixa',
        formSubtitle: "Què és, què costa i d’on ve",
        whatItIs: "Què és",
        whatItCosts: "Què costa i a quant es ven",
        whereItComesFrom: "D’on ve",
        readOnlyNote: "Aquesta fitxa és un mirall del magatzem: es llegeix aquí i es corregeix allà. Dos llocs per al mateix preu és un lloc on el preu està malament.",
        notFound: "Aquest article ja no existeix",
        leaveTitle: "Deixar la tarifa a mitges",
        leaveBody: "El que has escrit es perdrà. Una tarifa a mig omplir no es desa sola.",
        leaveConfirm: "Sortir sense desar",
        fields: {
          name: 'Nom', description: 'Descripció', kind: 'Tipus', unit: 'Unitat',
          price: 'Preu de venda', costPrice: 'Cost', costHint: "Deixa'l a 0 si no el sabeu: és millor que un marge inventat",
          taxRate: 'IVA', reference: 'Referència interna',
          supplier: 'Proveïdor', supplierRef: 'Ref. del proveïdor', leadTimeDays: 'Termini (dies)',
        },
        whoProvidesIt: 'Qui el presta',
        whoProvidesItHint: 'Un servei se subcontracta: qui el fa i quant triga a estar-hi.',
        newCategory: 'Nova família…',
        newCategoryLabel: 'Nom de la família',
        otherUnit: 'Una altra…',
        otherUnitLabel: 'Escriu la unitat',
        unitHint: 'S’imprimeix al pressupost, darrere del preu.',
        units: {
          hour: { value: 'h', label: 'h (hora)' },
          workday: { value: 'jornada', label: 'jornada (dia de feina)' },
          unit: { value: 'u.', label: 'u. (unitat)' },
          day: { value: 'dia', label: 'dia' },
          km: { value: 'km', label: 'km' },
          metre: { value: 'm', label: 'm (metre)' },
          sqm: { value: 'm²', label: 'm² (metre quadrat)' },
          cbm: { value: 'm³', label: 'm³ (metre cúbic)' },
          kg: { value: 'kg', label: 'kg (quilo)' },
          litre: { value: 'l', label: 'l (litre)' },
        },
      },

      templates: {
        edit: 'Editar plantilla', create: 'Crear plantilla', saveChanges: 'Desar canvis',
        estimatedTotals: 'Totals estimats', globalMargin: 'Marge global',
        bom: 'Components BOM', addManual: '+ Manual', supplierRef: 'Proveïdor · Ref',
        searchCatalog: 'Cercar catàleg', searchLabour: "Cercar mà d'obra",
        searchCatalogPlaceholder: 'Cercar peça, component, servei…',
        searchLabourPlaceholder: 'Cercar lloc o especialitat…',
        searchNoResults: 'Sense resultats per a «{query}»',
        searchHint: 'Escriu almenys 2 caràcters per cercar',
        noComponents: 'Sense components — prem «+ Manual» o cerca al catàleg',
        noLabour: 'Sense llocs — prem «+ Lloc» o cerca al catàleg',
        addRole: '+ Lloc',
        title: 'Plantilles',
        subtitle: 'Màquines i conjunts que ja heu pressupostat',
        new: 'Nova plantilla',
        formHint: "El que es desa aquí és el que la IA fa servir de referència per pressupostar quelcom semblant.",
        identity: 'Identificació',
        searchPlaceholder: 'Nom o descripció…',
        components: 'Components', addComponent: 'Afegir component',
        labour: "Mà d'obra", addLabour: 'Afegir lloc',
        labourHint: "Hores i cost per hora. El que es cobra per hora surt del multiplicador dels ajustos.",
        hours: 'Hores', componentCost: 'Cost de materials', labourCost: "Cost de mà d'obra",
        totalCost: 'Cost total', salePrice: 'Preu de venda',
        annex: 'Annex tècnic',
        annexHint: "Descripció que es pot imprimir a l'annex del pressupost.",
        annexDoc: 'Annex de la màquina',
        annexPhotoLater: 'Desa la plantilla per poder-hi pujar fotos.',
        fromQuote: 'Des de pressupost',
        empty: 'Encara no hi ha plantilles',
        emptyHint: "En marcar un pressupost com a acceptat se t'oferirà desar-lo com a plantilla.",
        emptyFiltered: 'Sense resultats', emptyFilteredHint: 'Prova de treure algun filtre.',
        nameRequired: 'Falta el nom', saved: 'Plantilla desada', removed: 'Plantilla eliminada',
        removeTitle: 'Eliminar plantilla', removeBody: 'Vols eliminar «{name}»? Els pressupostos fets amb ella no canvien.',
        fields: {
          name: 'Nom', description: 'Descripció',
          namePlaceholder: "Ex.: Envasadora flow pack 120 ud/min",
          tags: 'Etiquetes (separades per comes)', tagsHint: 'Ex.: envasat, flow pack, alimentació',
          rolePlaceholder: 'Ex.: Enginyeria / Disseny',
          descriptionHint: 'Com millor la descriguis, millor la troba la IA',
          reference: 'Referència', component: 'Component',
          role: 'Lloc', hours: 'Hores', rate: 'Cost/h', cost: 'Cost',
        },
      },

      rules: {
        title: 'Regles tècniques',
        subtitle: 'Els criteris que apliqueu en pressupostar',
        new: 'Nova regla', edit: 'Editar regla',
        formHint: "Escriu-la com l'explicaries a algú que entra nou: quan aplica, què cal fer i què passa si s'oblida.",
        identity: "Quina regla és",
        body: "Què diu",
        leaveTitle: "Deixar la regla a mitges",
        leaveBody: "El que has escrit es perdrà. Una regla a mig redactar no es desa sola.",
        leaveConfirm: "Sortir sense desar",
        notFound: "Aquesta regla ja no existeix",
        searchPlaceholder: 'Cercar a les regles…',
        learned: 'Apresa',
        empty: 'Encara no hi ha regles',
        emptyHint: 'És el que sap el vostre tècnic més veterà i no està escrit enlloc.',
        emptyFiltered: 'Sense resultats', emptyFilteredHint: 'Prova de treure algun filtre.',
        required: 'Falten el nom o la regla', saved: 'Regla desada', removed: 'Regla eliminada',
        removeTitle: 'Eliminar regla', removeBody: 'Vols eliminar «{title}»?',
        fields: {
          title: 'Nom', titlePlaceholder: 'Ex.: Tirada de canonada de més de 30 m',
          context: 'Quan aplica', contextHint: 'La situació en què cal tenir-la en compte',
          rule: 'Què cal fer', ruleHint: "Què s'inclou, s'afegeix o es comprova",
          consequence: "Què passa si s'omet", example: 'Exemple',
        },
      },

      learning: {
        title: 'Aprenentatge',
        subtitle: 'El que el sistema ha observat als vostres pressupostos, esperant el vostre criteri',
        refresh: 'Recalcular', refreshed: 'Recalculat',
        reindex: 'Reindexar', reindexed: '{count} elements reindexats',
        noSemantic: "La cerca per significat està desactivada en aquesta instal·lació: es cerca per text. Tot funciona, però troba menys.",
        tabs: { pending: 'Pendents', accepted: 'Aplicades', dismissed: 'Descartades' },
        metrics: {
          proposed: 'Línies proposades', acceptance: 'Es queden',
          unedited: 'Sense tocar el preu', avgEdit: 'Correcció mitjana',
        },
        kinds: {
          'price-adjust': 'Ajust de preu',
          'new-rate-item': 'Falta al catàleg',
          'stop-proposing': 'Deixar de proposar',
          'template-from-quote': 'Plantilla nova',
        },
        claims: {
          'price-adjust': 'Heu corregit «{concept}» {samples} vegades: la IA proposa {avgProposed} i acaba a {medianFinal} ({medianDeltaPct} %).',
          'new-rate-item': "«{name}» s'escriu a mà a {samples} pressupostos i no és al catàleg. Preu habitual: {medianPrice}.",
          'stop-proposing': "«{concept}» s'ha proposat i tret {rejected} vegades, i no se n'ha quedat cap.",
          'template-from-quote': 'El pressupost {code} ({grandTotal}) es va guanyar. Pot servir de referència per als propers.',
        },
        effects: {
          'price-adjust': 'Actualitza el preu al catàleg',
          'new-rate-item': "Crea l'article al catàleg",
          'stop-proposing': 'Crea una regla tècnica',
          'template-from-quote': 'Crea la plantilla',
        },
        evidence: 'Veure les {count} vegades', rejectedTimes: 'treta {count} vegades',
        accept: 'Aplicar', dismiss: 'Descartar',
        accepted: 'Aplicat', dismissed: 'Descartat',
        empty: {
          pending: 'Res a revisar',
          accepted: 'Encara no heu aplicat res',
          dismissed: 'Res descartat',
        },
        emptyHint: {
          pending: 'Apareixeran coses aquí quan el sistema vegi un patró: un preu que corregiu sempre, alguna cosa que escriviu a mà una vegada i una altra.',
          accepted: "El que accepteu s'aplicarà al catàleg o a les regles i quedarà llistat aquí.",
          dismissed: 'El que descarteu no es tornarà a proposar.',
        },
      },

      catalogPanel: {
        label: 'Catàleg', open: 'Obrir el catàleg', close: 'Amagar el catàleg',
        materials: 'Materials', rates: 'Tarifes',
        search: 'Cercar al catàleg…', allCategories: 'Totes les categories',
        loading: 'Carregant…', noResults: 'Sense resultats',
        dragHint: "Arrossega «{name}» a una línia, o fes clic per afegir-lo",
        failed: "No s'ha pogut carregar el catàleg.",
        retry: 'Tornar-ho a provar',
        partial: "S'estan mirant {shown} de {total} articles. Cerca al catàleg complet per veure la resta.",
      },

      annexAi: {
        tab: 'Assistent', title: 'Assistent de l’annex', status: 'Llest', newChat: 'Començar de nou',
        emptyTitle: 'Què ha de dir aquest annex?',
        emptyHint: 'Explica-l’hi com l’hi explicaries a un company. Escriu el document sencer o retoca el que ja hi ha.',
        placeholder: 'Escriu-me l’annex, o digues què canviar…',
        tags: { write: 'Escriure', table: 'Taula', terms: 'Condicions', exclusions: 'Exclusions', tidy: 'Retocar' },
        examples: {
          write: 'Escriu l’annex d’un quadre de maniobra per a quatre motors amb variador, PLC i pantalla.',
          writeFor: 'Escriu l’annex de «{name}»',
          tableFrom: 'Afegeix una taula de característiques a partir dels seus {n} components',
          intro: 'Escriu la presentació i l’abast d’aquest pressupost',
          terms: 'Escriu les condicions: termini, garantia i manera de treballar',
          table: 'Afegeix una taula amb les característiques principals.',
          exclusions: 'Afegeix les exclusions habituals d’aquest tipus de feina.',
          shorter: 'Escurça el segon paràgraf i treu la palla.',
        },
        proposal: 'Proposa {n} bloc(s)', apply: 'Aplicar', discard: 'Descartar', applied: 'Aplicat al document.',
        review: {
          title: 'Proposa {n} bloc(s). Repassa\'ls:',
          all: 'Marcar-los tots', none: 'Desmarcar-los tots',
          apply: 'Aplicar-ne {n} al document',
          applied: '{n} bloc(s) aplicats al document.',
          help: 'El que marquis serà el document. El que desmarquis no s\'hi queda, i després ho pots retocar tot a la columna del document.',
          state: { new: 'Nou', changed: 'Canviat', same: 'Igual' },
        },
        done: 'Llest.',
        warnings: {
          truncated: "S'ha tallat a mitges: el que has demanat no hi cap de cop. Demana-ho per parts —primer què se subministra, després les característiques— o escurça la instrucció.",
          unparseable: 'La resposta ha arribat trencada i no he pogut fer-la servir. Torna-ho a provar.',
          empty: "No n'ha sortit res. Explica-m'ho d'una altra manera.",
        },
      },
      annexDoc: {
        title: 'Annex tècnic',
        intro: 'Presentació i abast',
        closing: 'Condicions i exclusions',
        saveFirst: 'Desa el pressupost per poder-hi pujar fotos.',
      },

      annex: {
        title: 'Annex tècnic',
        edit: 'Editar', write: 'Escriure annex',
        blocks: '{n} bloc(s)',
        emptyCta: 'Encara no hi ha annex. Prem per escriure-hi.',
        writing: 'Document', resize: 'Arrossega per repartir; doble clic ho torna',
        asPrinted: 'Com sortirà imprès',
        panels: { hide: 'Tancar «{name}»', show: 'Obrir «{name}»', reopen: 'Tancades' },
        rendering: 'Dibuixant…', emptyPreview: 'Encara no hi ha res a ensenyar.',
        vars: {
          title: 'Dades que s’emplenen',
          hint: {
            template: 'El mateix annex serveix per a la màquina de 15 kW i per a la de 400. Escriu la clau entre claus dobles dins del text i declara-la aquí.',
            quote: 'Els buits que demana aquest annex. El que deixis buit s’imprimeix amb les claus posades.',
          },
          add: '+ Buit', key: 'clau', label: 'Com es llegeix', unit: 'Unitat',
          none: 'Aquest annex no demana cap dada.',
          loose: 'El text fa servir buits sense declarar:',
          looseAnswer: 'Buits escrits al text. Contesta\'ls aquí; el que quedi buit surt amb les claus posades.',
        },
        auto: {
          title: 'Dades del pressupost que s\'omplen soles',
          hint: 'Prem-ne una per copiar-la i enganxa-la al text. En imprimir es canvia per la dada d\'aquest pressupost.',
          copied: 'Copiat {hole}. Enganxa\'l al text.',
          groups: { quote: 'Pressupost', customer: 'Client', company: 'La teva empresa' },
          keys: {
            quote_code: 'Codi',
            quote_title: 'Títol',
            quote_date: 'Data',
            quote_validUntil: 'Vàlid fins',
            quote_subtotal: 'Base imposable',
            quote_total: 'Total',
            quote_paymentTerms: 'Forma de pagament',
            customer_name: 'Nom',
            customer_contact: 'Persona de contacte',
            company_name: 'Nom',
            company_taxId: 'NIF',
            company_phone: 'Telèfon',
            company_email: 'Correu',
            today: 'Data d\'avui',
          },
        },
        types: { heading: 'Títol', paragraph: 'Paràgraf', list: 'Llista', table: 'Taula', image: 'Foto' },
        up: 'Pujar', down: 'Baixar', remove: 'Treure',
        empty: 'L’annex és buit. Afegeix un bloc per començar.',
        headingPlaceholder: 'Abast del subministrament',
        paragraphPlaceholder: 'Què se subministra, com s’executa i què no hi entra.',
        itemPlaceholder: 'Una condició, una exclusió…', addItem: '+ Línia',
        header: 'Columna', addRow: '+ Fila', addColumn: '+ Columna', removeColumn: '− Columna',
        noPhoto: 'Sense foto', upload: 'Puja una foto', uploading: 'Pujant…', choose: 'Tria’n una de pujada',
        captionPlaceholder: 'Peu de foto',
      },
      composer: {
        resize: "Arrossega per canviar l'amplada; doble clic per restaurar-la",
        tabs: "Què s'està veient",
        tab: { quote: 'Pressupost', catalog: 'Catàleg', assistant: 'Assistent' },
        replies: {
          done: 'Fet: {n} línia/es preparades. Marca les que vulguis i afegeix-les.',
          empty: 'No he pogut preparar res. Prova de descriure-ho amb una mica més de detall.',
          truncated: 'S\'ha tallat a mitja resposta: la feina és llarga. Demana-m\'ho per parts o descriu-ho més curt.',
          retry: 'No he sabut donar-te una resposta utilitzable. Torna-ho a provar i, si insisteix, descriu-ho d\'una altra manera.',
          'no-credit': 'El compte d’IA d’aquesta instància s’ha quedat sense saldo, així que no puc redactar res. Avisa qui la porti: fins que la recarreguin, tornar-ho a provar no funcionarà.',
          'no-target': 'No trobo aquesta línia al pressupost. Digues-me com es diu exactament.',
          'no-selection': 'Selecciona abans una línia amb el punt de la seva esquerra.',
          'empty-quote': 'El pressupost és buit: no hi ha res de què fer una variant.',
          idle: "En què t'ajudo amb aquest pressupost?",
        },
        label: 'Assistent', open: "Obrir l'assistent", collapse: 'Amagar',
        expand: 'Ampliar', restore: 'Restaurar',
        title: 'Assistent de pressupostos', status: 'A punt',
        newChat: 'Nova conversa',
        emptyTitle: 'Què cal pressupostar?',
        emptyHint: 'Descriu-ho com ho explicaries a un company. Les línies es generen amb el vostre catàleg, les vostres plantilles i les vostres regles.',
        placeholder: 'Descriu la màquina, el conjunt o el que cal afegir…',
        selected: 'Seleccionada: <strong>{name}</strong>',
        selectedHint: '— digues-li què fer-ne',
        selectedOf: '{n} de {total} seleccionades',
        all: 'Tot', none: 'Res',
        include: 'Incloure aquesta línia',
        parts: '{n} components',
        fromTemplate: 'Trobat a les vostres plantilles',
        willAddTo: "S'afegirà dins de «{name}»",
        addToQuote: 'Afegir al pressupost',
        replaceVariant: 'Reemplaçar per aquesta variant',
        added: 'Afegit al pressupost',
        thinking: {
          start: 'Pensant…', reading: 'Analitzant la teva petició…',
          catalogue: 'Cercant al catàleg…', knowledge: 'Consultant plantilles i regles…',
          pricing: 'Calculant preus i marges…', writing: 'Preparant la proposta…',
        },
        examples: {
          wholeLabel: 'Màquina',
          whole: "Genera el pressupost d'una premsa hidràulica de 150 t amb PLC i panell HMI",
          templateLabel: 'Plantilla',
          template: 'Afegeix la màquina estàndard que solem fer',
          assemblyLabel: 'Conjunt',
          assembly: 'Quadre elèctric: PLC, 6 bornes de 2,5 mm², carril DIN i canaleta',
          addLabel: 'Afegir',
          add: 'Afegeix les partides de projecte tècnic, legalització i posada en marxa',
          wholeReal: 'Pressuposta «{name}»',
          templateReal: 'Afegeix «{name}» al pressupost',
          assemblyReal: 'Munta un conjunt amb {items}',
          addReal: 'Afegeix {items}',
        },
      },
      review: {
        title: 'Revisió tècnica', subtitle: "Què pot faltar abans d'enviar-lo",
        working: 'Analitzant el pressupost…',
        clean: 'El pressupost sembla complet: no es veu que falti res.',
        addSuggested: "Afegir «{name}»",
      },
      search: { subtitle: 'Obrir el pressupost' },
      pdf: { taxId: 'NIF' },
      notifications: {
        categories: {
          expired: 'Un pressupost ha caducat',
          accepted: 'Un pressupost ha estat acceptat',
        },
      },
    },

    modules: { quotes: 'Pressupostos' },
    assistant: {
      openers: {
        quotes: {
          'how-are-quotes-going': 'Com van els pressupostos aquest mes?',
          'waiting-for-an-answer': 'Quins pressupostos estan esperant resposta?',
          'draft-a-quote': "Prepara'm un pressupost del que et descriuré",
        },
      },
    },
    permissions: {
      quotes: {
        read: 'Veure pressupostos, catàleg i plantilles',
        write: 'Crear i editar pressupostos, i fer servir la IA',
        costs: 'Veure i canviar costos i marges',
        send: 'Enviar un pressupost al client',
        catalog: 'Canviar preus del catàleg',
        knowledge: 'Editar plantilles, regles i aprenentatge',
        import: 'Importar la tarifa des d\'un Excel',
      },
    },
    settings: {
      ns: {
        quotes: {
          title: 'Pressupostos',
          subtitle: 'Com pressuposta aquesta empresa',
          acceptFromPortal: {
            label: 'Que el client accepti des del seu portal tanca el pressupost',
            help: 'La seva resposta canvia l\'estat i l\'anuncia, igual que si ho hagués premut algú d\'aquí. Només fa alguna cosa amb el portal de client instal·lat.',
          },
          trade: {
            label: 'Què fabrica o instal·la aquesta empresa',
            help: 'Dues o tres línies amb les vostres paraules. És el que fa que la IA sàpiga de què parleu.',
          },
          defaultTaxRate: { label: 'IVA amb què neix un pressupost' },
          defaultCurrency: { label: 'Moneda dels pressupostos', help: 'ISO 4217, tres lletres.' },
          defaultValidityDays: {
            label: 'Dies de validesa d\'un pressupost',
            help: 'Passada la data es marca caducat sol. Posa-ho a 0 perquè no caduquin.',
          },
          defaultPaymentTerms: { label: 'Forma de pagament per defecte' },
          minMarginPct: {
            label: "Marge per sota del qual s'avisa abans d'enviar",
            help: "Només avisa: mai impedeix enviar res, s'assegura que ho heu vist.",
          },
          labourMarkup: {
            label: "Multiplicador del cost d'una hora al que es cobra",
            help: '1,5 vol dir que una hora que costa 40 es cobra a 60.',
          },
        },
      },
    },
    audit: {
      actions: {
        quotes: {
          quote_created: 'Pressupost creat',

          quote_draft: 'Pressupost tornat a esborrany',
          quote_updated: 'Pressupost modificat',
          quote_sent: 'Pressupost enviat',
          quote_emailed: 'Pressupost enviat per correu',
          quote_accepted: 'Pressupost acceptat',
          quote_linked_to_project: 'Pressupost vinculat a un projecte',
          quote_rejected: 'Pressupost rebutjat',
          quote_expired: 'Pressupost caducat',
          quote_cancelled: 'Pressupost anul·lat',
          quote_duplicated: 'Pressupost duplicat',
          quote_archived: 'Pressupost arxivat',
          quote_restored: 'Pressupost retornat a les llistes',
          rate_item_created: 'Article afegit al catàleg',
          rate_item_updated: 'Article del catàleg modificat',
          rate_item_deactivated: 'Article donat de baixa',
          rate_items_imported: 'Tarifa importada des d\'un fitxer',
          import_profile_created: 'Perfil d\'importació creat',
          import_profile_updated: 'Perfil d\'importació modificat',
          import_profile_deleted: 'Perfil d\'importació eliminat',
          template_created: 'Plantilla creada',
          template_updated: 'Plantilla modificada',
          template_deleted: 'Plantilla eliminada',
          rule_created: 'Regla tècnica creada',
          rule_updated: 'Regla tècnica modificada',
          rule_deleted: 'Regla tècnica eliminada',
          suggestion_accepted: "Suggeriment d'aprenentatge aplicat",
          suggestion_dismissed: "Suggeriment d'aprenentatge descartat",
        },
      },
    },
  },

  en: {
    errors: {
      ALREADY_DECIDED: 'That suggestion has already been decided.',
      EMPTY_QUOTE: 'A quote with no lines cannot become a template.',
      INVALID_TRANSITION: 'The quote cannot move to that status from the one it is in.',
      MATERIALS_OWNED_BY_INVENTORY: 'New materials are registered in Inventory on this instance.',
      NOTHING_TO_SEND: 'This quote has no line with a description and a price: it cannot be sent or accepted.',
      OWNED_BY_INVENTORY: 'This material is kept by Inventory. Change it there.',
      PROFILE_NAME_TAKEN: 'There is already an import profile with that name.',
      QUOTE_CHANGED: 'Somebody else changed this quote meanwhile. Reload it and try again.',
      QUOTE_LOCKED: 'An accepted quote cannot be edited. Duplicate it instead.',
      UNKNOWN_SUGGESTION: 'That kind of suggestion does not exist.',
    },
    quotes: {
      title: 'Quotes',
      help: {
        title: "Quotes",
        subtitle: "The rate catalogue as the base, templates and rules as the memory, and where the assistant gets its prices",
      },
      dashboard: {
        openAmount: 'Quoted and live',
        openAmountHelp: 'Drafts and sent quotes added up, in euros: what is on the table.',
        sent: 'Awaiting an answer',
        sentHelp: 'How many quotes have gone to the customer and have had no answer yet.',
        winRate: 'Win rate',
        winRateHelp: 'Of the quotes that got an answer, what percentage was accepted. The ones still waiting do not count.',
        wonAmount: 'Won',
        wonAmountHelp: 'Accepted quotes added up, in euros.',
        awaiting: 'No answer yet',
        awaitingHelp: 'The sent quotes still waiting for a reply, newest first. Stretch the card and more fit.',
      },
      subtitle: 'What you offer and at what price',
      actions: {
        new: 'New quote', edit: 'Edit', save: 'Save', cancel: 'Cancel',
        remove: 'Delete', archive: 'Archive', restore: 'Bring back', duplicate: 'Duplicate',
        pdf: 'Download PDF', send: 'Send to customer', confirmSend: 'Send',
        markAccepted: 'Mark accepted', markRejected: 'Mark rejected',
        saveAsTemplate: 'Save as template',
        sendAgain: 'Send again', markSent: 'Mark as sent',
        writeEmail: 'Write the email',
      },
      status: {
        draft: 'Draft', sent: 'Sent', accepted: 'Accepted',
        rejected: 'Rejected', expired: 'Expired', cancelled: 'Cancelled',
      },
      filters: {
        oneCustomer: 'One customer only',

        search: 'Search', searchPlaceholder: 'Code, title or customer…',
        status: 'Status', allStatuses: 'All', sort: 'Sort by',
      },
      sort: { createdAt: 'Newest', updatedAt: 'Last edited', total: 'Amount', validUntil: 'Validity' },
      table: {
        quote: 'Quote', customer: 'Customer', total: 'Amount', margin: 'Margin',
        validUntil: 'Valid until', status: 'Status', noCustomer: 'No customer',
      },
      kpi: { open: 'Open', awaiting: 'Awaiting an answer', won: 'Won', winRate: 'Win rate' },
      empty: {
        none: 'No quotes yet',
        noneHint: 'Create the first one. You can describe the job and let the AI draft the lines.',
        filtered: 'Nothing found',
        filteredHint: 'Try clearing a filter or searching for something else.',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },

      detail: {
        title: 'Quote',
        actions: 'Actions',
        daysLeft: 'Expires in {days} days',
        expiredOn: 'Expired on {date}',
        sent: 'Quote sent',
        outcomeSaved: 'Outcome saved',
        duplicated: 'Copy created: {code}',
        savedAsTemplate: 'Template "{name}" created',
      },
      form: {
        subtitle: 'Describe the job or drag from the catalogue; the AI drafts the lines',
        subtitlePhone: 'Describe the job to the assistant or pick from the catalogue',
        sectionInfo: 'Details',
        saving: 'Saving…', saveDraft: 'Save draft', saveAndSend: 'Save and send',
        savedAsDraft: 'Saved as a draft',
        review: 'Review', reviewing: 'Reviewing…',
        reviewNeedsLines: 'Add at least one line before reviewing',
        linesRequired: 'Add at least one line with a description',
        badLine: 'Check the quantity and price of "{name}"',
        newTitle: 'New quote', editTitle: 'Edit quote',
        newHint: 'Only the title is required; the rest can be filled in later',
        details: 'Quote details', notes: 'Notes',
        titleRequired: 'The title is missing',
        created: 'Quote created', saved: 'Quote saved',
      },
      fields: {
        customerPlaceholder: 'Company or private customer', paymentTermsPlaceholder: 'e.g. 50 % on order, 50 % on delivery',
        project: 'Project', projectPlaceholder: 'Which project it belongs to',
        title: 'Title', titlePlaceholder: 'e.g. Bottling line, 2,000 bottles/h',
        customer: 'Customer', contact: 'Contact person',
        date: 'Date',
        validUntil: 'Valid until', taxRate: 'VAT', paymentTerms: 'Payment terms',
        notes: 'Internal notes', notesHint: 'These do not go on the PDF',
        notesForClient: 'Notes for the customer', notesForClientHint: 'Printed on the quote',
      },
      lines: {
        dragHint: 'Drag a catalogue item here to add it as a line',
        unit: 'Unit', subtotal: 'Subtotal', ai: 'AI', marginShort: 'Mg%',
        dragToReorder: 'Drag to reorder', select: 'Select line',
        editMargin: 'Click to edit the margin', noCost: 'No cost set',
        manual: 'Manual line', describe: 'Let the AI write it', describing: 'Writing…',
        annex: 'Technical annex for this item',
        annexPlaceholder: 'What is supplied, how it is done and what is not included. It goes in the PDF annex.',
        breakdown: 'Material breakdown ({count} components)',
        addingComponentsTo: 'Adding components to',
        untitledLine: 'Untitled line',
        componentsAdded: '{n} added · last: {name}',
        componentsDone: 'Done',
        component: 'Component / material', componentsTotal: 'Components total',
        title: 'Lines', description: 'Description', descriptionPlaceholder: 'Item or assembly',
        quantity: 'Qty', unitPrice: 'Price', costPrice: 'Cost', discount: 'Disc. %',
        margin: 'Margin', total: 'Total',
        add: 'Add a line', addComponent: 'Add a component', remove: 'Remove',
        moveUp: 'Move up', moveDown: 'Move down',
        components: '{count} components', fromAi: 'AI', untitled: 'untitled',
        fromComponents: 'Calculated from the components',
        none: 'No lines yet',
        noneHint: 'Add one by hand, or describe the job in the panel on the right.',
      },
      totals: {
        globalMargin: 'Global margin', applyAll: 'Apply to all',
        marginRequired: 'Type a margin percentage before applying it',
        noPricedLines: 'No lines have a price to apply a margin to',
        confirmGlobalMargin: 'This sets the margin to {percent} % on {count} line(s), overwriting their costs. Continue?',
        title: 'Totals', subtotal: 'Subtotal', discount: 'Discounts',
        tax: 'VAT', taxAt: 'VAT {rate} %', grandTotal: 'Total', margin: 'Margin',
      },

      ai: {
        title: 'Draft with AI',
        intro: "Describe the job and the lines are drafted from the company's catalogue, templates and rules.",
        describe: 'What needs quoting',
        placeholder: 'e.g. 100 t hydraulic press with electrical panel, S7-1200 PLC and commissioning',
        generateQuote: 'A whole quote',
        generateAssembly: 'One assembly',
        addToSelected: 'Add to "{name}"',
        proposalTitle: '{count} lines proposed',
        grounded: '{pct} % on your own prices',
        apply: 'Add {count} to the quote',
        discard: 'Discard',
        applied: '{count} lines added',
        added: '{count} components added',
        review: 'Check what is missing',
        reviewClean: 'Nothing obvious is missing.',
        warning: {
          empty: 'Nothing came back. Try describing the job in more detail.',
          truncated: 'The answer was cut off by length. Break the request into smaller parts.',
          unparseable: 'The answer did not come back in a usable format. Try again.',
        },
      },

      preflight: {
        backAndFix: 'Go back and fix', fixFirst: 'Fix the errors', sendAnyway: 'Send anyway',
        summaryErrors: '{errors} error(s) and {warnings} warning(s)',
        summaryWarnings: '{warnings} warning(s), nothing that stops the send',
        summaryClean: 'All clear',
        groups: { info: 'Details', lines: 'Lines', margin: 'Margins' },
        title: 'Before it goes out',
        intro: 'A quick check. Warnings do not stop you sending it; errors do.',
        items: {
          title: 'Quote title',
          customer: 'Customer assigned',
          validUntil: 'Validity date',
          paymentTerms: 'Payment terms',
          lines: '{count} lines with a description and a price',
          emptyLines: '{count} lines with no description or a price of 0',
          negativeMargin: '{count} lines whose cost is at or above the sale price',
          thinMargin: '{count} lines below {threshold} % margin',
          averageMargin: 'Average margin on this quote: {margin} %',
          averageMarginHidden: 'Average margin on this quote (only people who can see costs see the figure)',
          unknownCost: '{count} lines with no cost: their real margin is unknown',
        },
      },
      // Lo que el correo que lleva el presupuesto dice de entrada: el asunto y
      // la plantilla, en el idioma del cliente (QuotePage → composeEmail). El
      // resto lo escribe quien lo manda en el redactor de la plataforma.
      send: {
        tplSubject: {
          standard: 'Quote {code} · {title}',
          followUp: 'Following up on quote {code}',
        },
        greeting: 'Hello {contact},',
        greetingAnon: 'Hello,',
        tplBody: {
          standard: 'Please find attached quote {code} for {title}, coming to {total}.\n\nIf you would like to go over any part of it, let me know.',
          followUp: 'A reminder about quote {code} we sent you, for {total}. I look forward to hearing from you.',
        },
        validitySentence: 'The price holds until {validUntil}.',
      },
      outcome: {
        title: 'Outcome',
        askTitle: 'What happened to this quote',
        askIntro: 'The reason is optional, but it is the only thing that lets the system learn from what is won and lost.',
        reasonLabel: 'Reason', noReason: 'Not given', noteLabel: 'Comment',
        reason: {
          price: 'Price', scope: 'Scope', timing: 'Timing',
          competitor: 'Competitor', other: 'Other',
        },
      },
      archive: {
        title: 'Archive quote',
        body: 'You are about to archive {code}. Nothing is deleted: it stops appearing in lists.',
        done: 'Quote archived',
        restored: 'Quote brought back',
        isArchived: 'This quote is archived: it is on no list. Bring it back to work with it again.',
      },

      pdfOptions: {
        emailTitle: 'Send the quote by email',
        emailIntro: 'How the attached PDF comes out, and which language the customer is written to in.',
        language: 'Customer\'s language',
        writeEmail: 'Write the email',
        languages: { es: 'Spanish', ca: 'Catalan', en: 'English', fr: 'French' },
        title: 'Generate the PDF',
        intro: 'Choose how much the document shows. It is asked every time because a technical office and a private customer do not read the same document.',
        generate: 'Generate the PDF',
        detail: {
          label: 'What each item shows',
          elements: 'The item and what it is made of',
          elementsHelp: 'The name and, underneath, the components it carries.',
          titles: 'Only the item name',
          titlesHelp: 'No description and no components: one line per item.',
        },
        prices: {
          label: 'Which prices appear',
          groups: 'One per item',
          groupsHelp: 'Price, discount and amount per item. Components without prices.',
          all: 'Every one, component by component',
          allHelp: 'Each component with its amount, which adds up to the unit price of the item.',
          allNeedsElements: 'Needs the components to be shown.',
          total: 'Only the total (closed price)',
          totalHelp: 'No amounts per item. The base, the tax and the total always appear.',
        },
        annex: {
          label: 'Explanatory annex of the installation',
          help: 'A separate sheet at the end, item by item, with what each one includes.',
        },
        summary: {
          detail: { elements: 'the items with their components', titles: 'only the item names' },
          groups: 'The document will show {detail}, with the amount of each item{annex}.',
          all: 'The document will show {detail}, with the amount of every component and of each item{annex}.',
          total: 'The document will show {detail}, with no amounts per item: only the total{annex}.',
          withAnnex: ', and the annex at the end',
        },
      },
      project: {
        title: 'Project',
        placeholder: 'Search the project…',
        save: 'Save link',
        linked: 'Quote linked to the project',
        unlinked: 'Quote unlinked from the project',
      },
      customer: {
        placeholder: 'Search for a customer, or type the name',
        searching: 'Searching…',
        noResults: 'No customer by that name. The name will be saved as written.',
        linked: 'File',
        unlink: 'Unlink from the customer file',
      },
      guide: {
        catalog: {
          filters: 'Search by name, reference or supplier. The two dropdowns narrow it by family and by kind: material, labour or service.',
          table: 'Each row is a rate. The margin works itself out from the sale price and the cost: in red it is almost always a cost typed into the price box. Click a row to correct it.',
          form: 'The cost never appears on the quote the customer reads: it is there for the margin, and so the AI does not price below it. Leave it at zero if you do not know it. Deactivating an item takes it out of the catalogue without touching the quotes that already use it.',
        },
        templates: {
          what: 'A template is an assembly already worked out —components and hours— that goes into a quote as a single line and carries its cost with it. You do not have to build them by hand: marking a quote as accepted offers to save it as one.',
          cards: 'Each card shows what you look for when reusing one: how many components it carries, how many hours, and the margin it went out at. Click one to open and adjust it.',
        },
        rules: {
          what: 'A rule is what your most senior engineer applies without being asked. They are read before every draft and travel with the request to the AI: this screen is where it is taught the trade.',
          list: 'Each rule reads the way one is said out loud: when it applies, what to do, and what happens if it is forgotten. The ones marked “Learned” were written by the module from watching what you correct, and they are edited or deleted like any other.',
        },
        learning: {
          actions: '“Recalculate” looks at the evidence again and rebuilds this list. “Reindex” rebuilds the search by meaning over the catalogue, the templates and the rules. Neither of them changes your data.',
          metrics: 'Four numbers for a single question: is this getting better. “Untouched price” is the one that answers it: how many proposed prices nobody had to correct.',
          tabs: 'Pending are the ones waiting for your judgement. Applied and dismissed stay in view, so what was decided and when is still there to read.',
          cards: 'Each card says what it noticed, over how many cases —“See the N times” opens the evidence— and exactly what it will do if you accept it. Nothing is applied on its own: that button is the only moment the module writes to your catalogue or your rules.',
        },
      },
      catalog: {
        pendingHandover: '{count} materials are still registered here. As the warehouse registers them, these rows convert themselves and this notice goes away.',
        fromInventory: 'From the warehouse',
        materialsElsewhere: 'Materials are kept by the inventory module: they show here and can be quoted, but they are registered and corrected there. This screen registers labour and services.',
        title: 'Rate catalogue',
        subtitle: 'What you sell and what it costs you',
        newIn: { rates: 'New rate', materials: 'New material' },
        editIn: { rates: 'Edit rate', materials: 'Edit material' },
        formSubtitleRate: 'What it is and what it costs',
        groups: {
          rates: 'Rates',
          ratesHint: 'Labour and services: an hour of a fitter, an hour of the workshop manager, a call-out.',
          materials: 'Materials',
          materialsHint: 'Parts and supplies that go into the job.',
        },
        category: 'Category', allCategories: 'All', kind: 'Kind', allKinds: 'All',
        kinds: { material: 'Material', labour: 'Labour', service: 'Service' },
        import: {
          open: 'Import from Excel',
          title: 'Import price list from Excel',
          file: 'File',
          fileHint: 'Excel (.xlsx, .xls) or CSV. Up to 8 MB.',
          profile: 'Profile',
          profileHint: 'How this spreadsheet is read. Saved once and reused for every year\'s price list.',
          newProfile: 'New: guess the columns',
          lastFile: 'Last price list imported with this profile: {name}.',
          download: 'Download',
          empty: 'Choose the price list file',
          emptyText: 'You see what it would do first. No price changes until you click Import.',
          sheet: 'Sheet',
          sheetRows: '{name} ({n} rows)',
          headerRow: 'Header row',
          headerRowHint: '0 to find it automatically.',
          columns: 'Which column is each field',
          notMapped: '— Not in the file —',
          pricing: 'Price',
          pricingHint: 'If the list carries the supplier\'s list price: their discount gives your cost, and your markup gives the sale price. A cost or sale column overrides this.',
          discount: 'Supplier discount (%)',
          markup: 'Your markup on cost (%)',
          defaults: 'For whatever the file does not say',
          decimal: 'Decimals',
          decimals: {
            auto: 'Detect',
            comma: 'Comma (1.234,56)',
            dot: 'Dot (1,234.56)',
          },
          matchBy: 'Recognise existing items by',
          match: {
            reference: 'Reference',
            name: 'Name',
          },
          reading: 'Reading the file…',
          count: {
            create: '{n} new',
            update: '{n} change price or details',
            unchanged: '{n} unchanged',
            errors: '{n} with problems',
            created: '{n} created',
            updated: '{n} updated',
          },
          missing: 'These profile columns are not in this file: {fields}. Pick another or leave it unassigned.',
          needsName: 'Choose which column is the item name.',
          rowNumber: 'Row',
          action: {
            create: 'New',
            update: 'Changes',
            unchanged: 'Same',
          },
          errorList: 'See the {n} rows with problems',
          errors: {
            NO_NAME: 'Has a price but no name',
            NO_PRICE: 'Has no price',
            NEGATIVE_PRICE: 'Negative price',
            DUPLICATE_IN_FILE: 'Repeated in the file (the last one counts)',
            OWNED_BY_INVENTORY: 'This material belongs to the warehouse: change its price there',
            MATERIALS_OWNED_BY_INVENTORY: 'On this instance materials are registered in the warehouse',
            other: 'Could not be imported ({code})',
          },
          profileName: 'Profile name (e.g. Aragón price list)',
          saveProfile: 'Save profile',
          updateProfile: 'Update profile',
          profileSaved: 'Profile «{name}» saved',
          run: 'Import {n}',
          done: 'Import finished',
          fields: {
            reference: 'Reference',
            name: 'Name',
            description: 'Description',
            category: 'Category',
            unit: 'Unit',
            kind: 'Type',
            costPrice: 'Cost',
            price: 'Sale price',
            listPrice: 'Supplier list price',
            supplier: 'Supplier',
            supplierRef: 'Supplier ref.',
            leadTimeDays: 'Lead time (days)',
            taxRate: 'VAT (%)',
          },
        },
        searchPlaceholder: 'Name, reference or supplier…',
        leadDays: '{days} days',
        empty: 'The catalogue is empty',
        emptyHint: 'This is the file that makes a quote take minutes instead of an afternoon.',
        emptyFiltered: 'Nothing found', emptyFilteredHint: 'Try clearing a filter.',
        nameRequired: 'The name is missing', saved: 'Item saved',
        deactivate: 'Deactivate',
        deactivateTitle: 'Deactivate the item',
        deactivateBody: '"{name}" will stop appearing in the catalogue. Quotes that use it are unchanged.',
        deactivated: 'Item deactivated',
        formSubtitle: "What it is, what it costs, and where it comes from",
        whatItIs: "What it is",
        whatItCosts: "What it costs and what it sells for",
        whereItComesFrom: "Where it comes from",
        readOnlyNote: "This row is a mirror of the warehouse: it is read here and corrected there. Two places for the same price is one place where the price is wrong.",
        notFound: "That item no longer exists",
        leaveTitle: "Leave the rate half filled in",
        leaveBody: "What you have written will be lost. A half-filled rate does not save itself.",
        leaveConfirm: "Leave without saving",
        fields: {
          name: 'Name', description: 'Description', kind: 'Kind', unit: 'Unit',
          price: 'Sale price', costPrice: 'Cost', costHint: 'Leave it at 0 if you do not know it: better than an invented margin',
          taxRate: 'VAT', reference: 'Internal reference',
          supplier: 'Supplier', supplierRef: 'Supplier ref.', leadTimeDays: 'Lead time (days)',
        },
        whoProvidesIt: 'Who provides it',
        whoProvidesItHint: 'A service is subcontracted: who does it, and how long it takes to be there.',
        newCategory: 'New family…',
        newCategoryLabel: 'Family name',
        otherUnit: 'Other…',
        otherUnitLabel: 'Type the unit',
        unitHint: 'It is printed on the quote, after the price.',
        units: {
          hour: { value: 'h', label: 'h (hour)' },
          workday: { value: 'day', label: 'day (working day)' },
          unit: { value: 'ea.', label: 'ea. (each)' },
          day: { value: 'day', label: 'day' },
          km: { value: 'km', label: 'km' },
          metre: { value: 'm', label: 'm (metre)' },
          sqm: { value: 'm²', label: 'm² (square metre)' },
          cbm: { value: 'm³', label: 'm³ (cubic metre)' },
          kg: { value: 'kg', label: 'kg (kilo)' },
          litre: { value: 'l', label: 'l (litre)' },
        },
      },

      templates: {
        edit: 'Edit template', create: 'Create template', saveChanges: 'Save changes',
        estimatedTotals: 'Estimated totals', globalMargin: 'Global margin',
        bom: 'Bill of materials', addManual: '+ Manual', supplierRef: 'Supplier · Ref',
        searchCatalog: 'Search catalogue', searchLabour: 'Search labour',
        searchCatalogPlaceholder: 'Search for a part, component or service…',
        searchLabourPlaceholder: 'Search for a role or trade…',
        searchNoResults: 'Nothing found for "{query}"',
        searchHint: 'Type at least 2 characters to search',
        noComponents: 'No components — press "+ Manual" or search the catalogue',
        noLabour: 'No roles — press "+ Role" or search the catalogue',
        addRole: '+ Role',
        title: 'Templates',
        subtitle: 'Machines and assemblies you have already quoted',
        new: 'New template',
        formHint: 'What you save here is what the AI uses as a reference when quoting something similar.',
        identity: 'Identification',
        searchPlaceholder: 'Name or description…',
        components: 'Components', addComponent: 'Add a component',
        labour: 'Labour', addLabour: 'Add a role',
        labourHint: 'Hours and cost per hour. What an hour is sold at comes from the multiplier in settings.',
        hours: 'Hours', componentCost: 'Materials cost', labourCost: 'Labour cost',
        totalCost: 'Total cost', salePrice: 'Sale price',
        annex: 'Technical annex',
        annexHint: 'A description that can be printed in the quote annex.',
        annexDoc: 'Machine annex',
        annexPhotoLater: 'Save the template before uploading photos to it.',
        fromQuote: 'From a quote',
        empty: 'No templates yet',
        emptyHint: 'When you mark a quote as accepted you will be offered the chance to keep it as a template.',
        emptyFiltered: 'Nothing found', emptyFilteredHint: 'Try clearing a filter.',
        nameRequired: 'The name is missing', saved: 'Template saved', removed: 'Template deleted',
        removeTitle: 'Delete template', removeBody: 'Delete "{name}"? Quotes made from it are unchanged.',
        fields: {
          name: 'Name', description: 'Description',
          namePlaceholder: 'e.g. Flow pack wrapper, 120 units/min',
          tags: 'Tags (comma separated)', tagsHint: 'e.g. packaging, flow pack, food',
          rolePlaceholder: 'e.g. Engineering / Design',
          descriptionHint: 'The better you describe it, the better the AI finds it',
          reference: 'Reference', component: 'Component',
          role: 'Role', hours: 'Hours', rate: 'Cost/h', cost: 'Cost',
        },
      },

      rules: {
        title: 'Technical rules',
        subtitle: 'The criteria you apply when quoting',
        new: 'New rule', edit: 'Edit rule',
        formHint: 'Write it the way you would explain it to somebody new: when it applies, what to do, and what goes wrong if it is forgotten.',
        identity: 'Which rule this is',
        body: 'What it says',
        leaveTitle: 'Leave the rule half written',
        leaveBody: 'What you have written will be lost. A half-written rule does not save itself.',
        leaveConfirm: 'Leave without saving',
        notFound: 'That rule no longer exists',
        searchPlaceholder: 'Search the rules…',
        learned: 'Learned',
        empty: 'No rules yet',
        emptyHint: 'This is what your most experienced engineer knows and nobody has written down.',
        emptyFiltered: 'Nothing found', emptyFilteredHint: 'Try clearing a filter.',
        required: 'The name or the rule is missing', saved: 'Rule saved', removed: 'Rule deleted',
        removeTitle: 'Delete rule', removeBody: 'Delete "{title}"?',
        fields: {
          title: 'Name', titlePlaceholder: 'e.g. Pipe runs over 30 m',
          context: 'When it applies', contextHint: 'The situation in which it has to be taken into account',
          rule: 'What to do', ruleHint: 'What gets included, added or checked',
          consequence: 'What happens if it is skipped', example: 'Example',
        },
      },

      learning: {
        title: 'Learning',
        subtitle: 'What the system has noticed in your quotes, waiting for your judgement',
        refresh: 'Recompute', refreshed: 'Recomputed',
        reindex: 'Reindex', reindexed: '{count} items reindexed',
        noSemantic: 'Search by meaning is off on this installation: it searches by text. Everything works, it just finds less.',
        tabs: { pending: 'Pending', accepted: 'Applied', dismissed: 'Dismissed' },
        metrics: {
          proposed: 'Lines proposed', acceptance: 'Kept',
          unedited: 'Price left alone', avgEdit: 'Average correction',
        },
        kinds: {
          'price-adjust': 'Price adjustment',
          'new-rate-item': 'Missing from the catalogue',
          'stop-proposing': 'Stop proposing',
          'template-from-quote': 'New template',
        },
        claims: {
          'price-adjust': 'You have corrected "{concept}" {samples} times: the AI proposes {avgProposed} and it ends up at {medianFinal} ({medianDeltaPct} %).',
          'new-rate-item': '"{name}" is typed by hand in {samples} quotes and is not in the catalogue. Usual price: {medianPrice}.',
          'stop-proposing': '"{concept}" has been proposed and removed {rejected} times, and kept none.',
          'template-from-quote': 'Quote {code} ({grandTotal}) was won. It could be a reference for the next one.',
        },
        effects: {
          'price-adjust': 'Updates the price in the catalogue',
          'new-rate-item': 'Creates the catalogue item',
          'stop-proposing': 'Creates a technical rule',
          'template-from-quote': 'Creates the template',
        },
        evidence: 'See the {count} occurrences', rejectedTimes: 'removed {count} times',
        accept: 'Apply', dismiss: 'Dismiss',
        accepted: 'Applied', dismissed: 'Dismissed',
        empty: {
          pending: 'Nothing to review',
          accepted: 'You have not applied anything yet',
          dismissed: 'Nothing dismissed',
        },
        emptyHint: {
          pending: 'Things appear here when the system sees a pattern: a price you always correct, something you type by hand over and over.',
          accepted: 'What you accept is applied to the catalogue or the rules and listed here.',
          dismissed: 'What you dismiss is never proposed again.',
        },
      },

      catalogPanel: {
        label: 'Catalogue', open: 'Open the catalogue', close: 'Hide the catalogue',
        materials: 'Materials', rates: 'Rates',
        search: 'Search the catalogue…', allCategories: 'All categories',
        loading: 'Loading…', noResults: 'Nothing found',
        dragHint: 'Drag "{name}" onto a line, or click to add it',
        failed: 'The catalogue could not be loaded.',
        retry: 'Try again',
        partial: 'Looking at {shown} of {total} items. Search the full catalogue to see the rest.',
      },

      annexAi: {
        tab: 'Assistant', title: 'Annex assistant', status: 'Ready', newChat: 'Start over',
        emptyTitle: 'What should this annex say?',
        emptyHint: 'Tell it the way you would tell a colleague. Write the whole document, or adjust the one that is there.',
        placeholder: 'Write me the annex, or tell me what to change…',
        tags: { write: 'Write', table: 'Table', terms: 'Terms', exclusions: 'Exclusions', tidy: 'Adjust' },
        examples: {
          write: 'Write the annex for a control panel for four motors with a drive, a PLC and an HMI.',
          writeFor: 'Write the annex for "{name}"',
          tableFrom: 'Add a specifications table from its {n} components',
          intro: 'Write the introduction and scope of this quote',
          terms: 'Write the terms: lead time, warranty and how the work is done',
          table: 'Add a table with the main specifications.',
          exclusions: 'Add the usual exclusions for this kind of job.',
          shorter: 'Shorten the second paragraph and cut the fluff.',
        },
        proposal: 'Proposes {n} block(s)', apply: 'Apply', discard: 'Discard', applied: 'Applied to the document.',
        review: {
          title: 'Proposes {n} block(s). Go through them:',
          all: 'Tick all', none: 'Untick all',
          apply: 'Apply {n} to the document',
          applied: '{n} block(s) applied to the document.',
          help: 'What you tick becomes the document. What you untick does not stay, and you can edit all of it afterwards in the document column.',
          state: { new: 'New', changed: 'Changed', same: 'Unchanged' },
        },
        done: 'Done.',
        warnings: {
          truncated: 'It was cut off halfway: what you asked for does not fit in one go. Ask for it in parts — what is supplied first, then the specifications — or shorten the instruction.',
          unparseable: 'The answer came back broken and could not be used. Try again.',
          empty: 'Nothing came out. Tell me in another way.',
        },
      },
      annexDoc: {
        title: 'Technical annex',
        intro: 'Introduction and scope',
        closing: 'Conditions and exclusions',
        saveFirst: 'Save the quote before uploading photos to it.',
      },

      annex: {
        title: 'Technical annex',
        edit: 'Edit', write: 'Write annex',
        blocks: '{n} block(s)',
        emptyCta: 'No annex yet. Click to write it.',
        writing: 'Document', resize: 'Drag to share the width; double click resets it',
        asPrinted: 'As it will print',
        panels: { hide: 'Close "{name}"', show: 'Open "{name}"', reopen: 'Closed' },
        rendering: 'Drawing…', emptyPreview: 'Nothing to show yet.',
        vars: {
          title: 'Values to fill in',
          hint: {
            template: 'The same annex serves the 15 kW machine and the 400 kW one. Write the key in double braces inside the text and declare it here.',
            quote: 'The values this annex asks for. Anything left empty prints with its braces showing.',
          },
          add: '+ Value', key: 'key', label: 'How it reads', unit: 'Unit',
          none: 'This annex asks for nothing.',
          loose: 'The text uses undeclared values:',
          looseAnswer: 'Placeholders written in the text. Fill them in here; anything left empty prints with its braces.',
        },
        auto: {
          title: 'Quote details that fill themselves in',
          hint: 'Click one to copy it and paste it into the text. When printed it becomes this quote\'s value.',
          copied: 'Copied {hole}. Paste it into the text.',
          groups: { quote: 'Quote', customer: 'Customer', company: 'Your company' },
          keys: {
            quote_code: 'Code',
            quote_title: 'Title',
            quote_date: 'Date',
            quote_validUntil: 'Valid until',
            quote_subtotal: 'Taxable base',
            quote_total: 'Total',
            quote_paymentTerms: 'Payment terms',
            customer_name: 'Name',
            customer_contact: 'Contact person',
            company_name: 'Name',
            company_taxId: 'Tax id',
            company_phone: 'Phone',
            company_email: 'Email',
            today: 'Today\'s date',
          },
        },
        types: { heading: 'Heading', paragraph: 'Paragraph', list: 'List', table: 'Table', image: 'Photo' },
        up: 'Move up', down: 'Move down', remove: 'Remove',
        empty: 'The annex is empty. Add a block to start.',
        headingPlaceholder: 'Scope of supply',
        paragraphPlaceholder: 'What is supplied, how it is done and what is not included.',
        itemPlaceholder: 'A condition, an exclusion…', addItem: '+ Item',
        header: 'Column', addRow: '+ Row', addColumn: '+ Column', removeColumn: '− Column',
        noPhoto: 'No photo', upload: 'Upload photo', uploading: 'Uploading…', choose: 'Pick one already uploaded',
        captionPlaceholder: 'Caption',
      },
      composer: {
        resize: 'Drag to change the width; double-click to reset it',
        tabs: 'What is being shown',
        tab: { quote: 'Quote', catalog: 'Catalogue', assistant: 'Assistant' },
        replies: {
          done: 'Ready: {n} line(s) prepared. Tick the ones you want and add them.',
          empty: 'I could not prepare anything. Try describing it in a little more detail.',
          truncated: 'It was cut off part way through: the job is a long one. Ask me for it in parts, or describe it more briefly.',
          retry: 'I could not produce a usable answer. Try again, and if it keeps happening, describe it a different way.',
          'no-credit': 'This instance has run out of AI credit, so I cannot draft anything. Tell whoever runs it: until it is topped up, trying again will not work.',
          'no-target': 'I cannot find that line in the quote. Tell me exactly what it is called.',
          'no-selection': 'Select a line first, using the dot to its left.',
          'empty-quote': 'The quote is empty, so there is nothing to make a variant of.',
          idle: 'What can I help you with on this quote?',
        },
        label: 'Assistant', open: 'Open the assistant', collapse: 'Hide',
        expand: 'Expand', restore: 'Restore',
        title: 'Quote assistant', status: 'Ready',
        newChat: 'New conversation',
        emptyTitle: 'What needs quoting?',
        emptyHint: 'Describe it the way you would to a colleague. The lines are drafted from your catalogue, your templates and your rules.',
        placeholder: 'Describe the machine, the assembly, or what to add…',
        selected: 'Selected: <strong>{name}</strong>',
        selectedHint: '— tell it what to do with it',
        selectedOf: '{n} of {total} selected',
        all: 'All', none: 'None',
        include: 'Include this line',
        parts: '{n} components',
        fromTemplate: 'Found in your templates',
        willAddTo: 'Will be added inside "{name}"',
        addToQuote: 'Add to the quote',
        replaceVariant: 'Replace with this variant',
        added: 'Added to the quote',
        thinking: {
          start: 'Thinking…', reading: 'Reading your request…',
          catalogue: 'Searching the catalogue…', knowledge: 'Checking templates and rules…',
          pricing: 'Working out prices and margins…', writing: 'Preparing the proposal…',
        },
        examples: {
          wholeLabel: 'Machine',
          whole: 'Draft a quote for a 150 t hydraulic press with a PLC and an HMI panel',
          templateLabel: 'Template',
          template: 'Add the standard machine we usually build',
          assemblyLabel: 'Assembly',
          assembly: 'Electrical panel: PLC, six 2.5 mm² terminals, DIN rail and trunking',
          addLabel: 'Add',
          add: 'Add the lines for technical project, legalisation and commissioning',
          wholeReal: 'Quote "{name}"',
          templateReal: 'Add "{name}" to the quote',
          assemblyReal: 'Put together an assembly with {items}',
          addReal: 'Add {items}',
        },
      },
      review: {
        title: 'Technical review', subtitle: 'What might be missing before it goes out',
        working: 'Going through the quote…',
        clean: 'The quote looks complete: nothing obvious is missing.',
        addSuggested: 'Add "{name}"',
      },
      search: { subtitle: 'Open the quote' },
      pdf: { taxId: 'Tax id' },
      notifications: {
        categories: {
          expired: 'A quote has expired',
          accepted: 'A quote has been accepted',
        },
      },
    },

    modules: { quotes: 'Quotes' },
    assistant: {
      openers: {
        quotes: {
          'how-are-quotes-going': 'How are our quotes doing this month?',
          'waiting-for-an-answer': 'Which quotes are still waiting for an answer?',
          'draft-a-quote': 'Draft a quote for a job I will describe',
        },
      },
    },
    permissions: {
      quotes: {
        read: 'See quotes, the catalogue and the templates',
        write: 'Create and edit quotes, and use the AI',
        costs: 'See and change costs and margins',
        send: 'Send a quote to a customer',
        catalog: 'Change catalogue prices',
        knowledge: 'Edit templates, rules and learning',
        import: 'Import the price list from a spreadsheet',
      },
    },
    settings: {
      ns: {
        quotes: {
          title: 'Quotes',
          subtitle: 'How this company quotes',
          acceptFromPortal: {
            label: 'A customer accepting in their portal closes the quote',
            help: 'Their decision changes the status and announces it, exactly as if somebody here had clicked it. Only does anything with the customer portal installed.',
          },
          trade: {
            label: 'What this company builds or installs',
            help: 'Two or three lines in your own words. It is what makes the AI know what you are talking about.',
          },
          defaultTaxRate: { label: 'Tax rate a new quote starts with' },
          defaultCurrency: { label: 'Currency quotes are issued in', help: 'ISO 4217, three letters.' },
          defaultValidityDays: {
            label: 'How many days a quote is valid for',
            help: 'Past that date it is marked expired on its own. Set it to 0 for no expiry.',
          },
          defaultPaymentTerms: { label: 'Default payment terms' },
          minMarginPct: {
            label: 'Margin below which a quote is flagged before sending',
            help: 'Only a warning: it never stops anybody sending a quote, it makes sure they saw it.',
          },
          labourMarkup: {
            label: 'Multiplier from the cost of an hour to what it is sold at',
            help: '1.5 means an hour costing 40 is sold at 60.',
          },
        },
      },
    },
    audit: {
      actions: {
        quotes: {
          quote_created: 'Quote created',

          quote_draft: 'Quote back to draft',
          quote_updated: 'Quote edited',
          quote_sent: 'Quote sent',
          quote_emailed: 'Quote emailed',
          quote_accepted: 'Quote accepted',
          quote_linked_to_project: 'Quote linked to a project',
          quote_rejected: 'Quote rejected',
          quote_expired: 'Quote expired',
          quote_cancelled: 'Quote cancelled',
          quote_duplicated: 'Quote duplicated',
          quote_archived: 'Quote archived',
          quote_restored: 'Quote brought back',
          rate_item_created: 'Catalogue item added',
          rate_item_updated: 'Catalogue item edited',
          rate_item_deactivated: 'Catalogue item deactivated',
          rate_items_imported: 'Price list imported from a file',
          import_profile_created: 'Import profile created',
          import_profile_updated: 'Import profile changed',
          import_profile_deleted: 'Import profile deleted',
          template_created: 'Template created',
          template_updated: 'Template edited',
          template_deleted: 'Template deleted',
          rule_created: 'Technical rule created',
          rule_updated: 'Technical rule edited',
          rule_deleted: 'Technical rule deleted',
          suggestion_accepted: 'Learning suggestion applied',
          suggestion_dismissed: 'Learning suggestion dismissed',
        },
      },
    },
  },

  fr: {
    errors: {
      ALREADY_DECIDED: 'Cette suggestion a déjà été tranchée.',
      EMPTY_QUOTE: 'Un devis sans lignes ne peut pas devenir un modèle.',
      INVALID_TRANSITION: 'Le devis ne peut pas passer à ce statut depuis celui où il se trouve.',
      MATERIALS_OWNED_BY_INVENTORY: 'Sur cette instance, les nouveaux matériaux se créent dans Inventaire.',
      NOTHING_TO_SEND: 'Ce devis n’a aucune ligne avec un libellé et un prix : il ne peut être ni envoyé ni accepté.',
      OWNED_BY_INVENTORY: 'Ce matériau est géré par Inventaire. Modifiez-le là-bas.',
      PROFILE_NAME_TAKEN: 'Un profil d’import porte déjà ce nom.',
      QUOTE_CHANGED: 'Quelqu’un d’autre a modifié ce devis entre-temps. Rechargez-le et réessayez.',
      QUOTE_LOCKED: 'Un devis accepté ne peut pas être modifié. Dupliquez-le.',
      UNKNOWN_SUGGESTION: 'Ce type de suggestion n’existe pas.',
    },
    quotes: {
      title: 'Devis',
      help: {
        title: "Devis",
        subtitle: "Le catalogue de tarifs comme base, les modèles et les règles comme mémoire, et d’où l’assistant sort ses prix",
      },
      dashboard: {
        openAmount: 'Devis en jeu',
        openAmountHelp: 'Brouillons et devis envoyés additionnés, en euros : ce qui est en jeu.',
        sent: 'En attente de réponse',
        sentHelp: 'Combien de devis sont partis chez le client et n\'ont pas encore de réponse.',
        winRate: 'Taux de réussite',
        winRateHelp: 'Parmi les devis ayant reçu une réponse, quel pourcentage a été accepté. Ceux qui attendent encore ne comptent pas.',
        wonAmount: 'Gagné',
        wonAmountHelp: 'Devis acceptés additionnés, en euros.',
        awaiting: 'Sans réponse',
        awaitingHelp: 'Les devis envoyés qui attendent encore une réponse, du plus récent au plus ancien. Étirez la carte et il en tient plus.',
      },
      subtitle: 'Ce que vous proposez et à quel prix',
      actions: {
        new: 'Nouveau devis', edit: 'Modifier', save: 'Enregistrer', cancel: 'Annuler',
        remove: 'Supprimer', archive: 'Archiver', restore: 'Remettre dans les listes', duplicate: 'Dupliquer',
        pdf: 'Télécharger le PDF', send: 'Envoyer au client', confirmSend: 'Envoyer',
        markAccepted: 'Marquer accepté', markRejected: 'Marquer refusé',
        saveAsTemplate: 'Enregistrer comme modèle',
        sendAgain: 'Renvoyer', markSent: 'Marquer comme envoyé',
        writeEmail: 'Rédiger le courriel',
      },
      status: {
        draft: 'Brouillon', sent: 'Envoyé', accepted: 'Accepté',
        rejected: 'Refusé', expired: 'Expiré', cancelled: 'Annulé',
      },
      filters: {
        oneCustomer: 'Un seul client',

        search: 'Rechercher', searchPlaceholder: 'Code, titre ou client…',
        status: 'Statut', allStatuses: 'Tous', sort: 'Trier par',
      },
      sort: { createdAt: 'Plus récents', updatedAt: 'Dernière modification', total: 'Montant', validUntil: 'Validité' },
      table: {
        quote: 'Devis', customer: 'Client', total: 'Montant', margin: 'Marge',
        validUntil: 'Valable jusqu\'au', status: 'Statut', noCustomer: 'Sans client',
      },
      kpi: { open: 'En cours', awaiting: 'En attente de réponse', won: 'Gagné', winRate: 'Taux de réussite' },
      empty: {
        none: 'Aucun devis pour le moment',
        noneHint: 'Crée le premier. Tu peux décrire le chantier et laisser l\'IA rédiger les lignes.',
        filtered: 'Aucun résultat',
        filteredHint: 'Essaie d\'enlever un filtre ou de chercher autre chose.',
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },

      detail: {
        title: 'Devis',
        actions: 'Actions',
        daysLeft: 'Expire dans {days} jours',
        expiredOn: 'Expiré le {date}',
        sent: 'Devis envoyé',
        outcomeSaved: 'Résultat enregistré',
        duplicated: 'Copie créée : {code}',
        savedAsTemplate: 'Modèle « {name} » créé',
      },
      form: {
        subtitle: 'Décris le chantier ou fais glisser du catalogue ; l\'IA rédige les lignes',
        subtitlePhone: "Décris le chantier à l'assistant ou choisis dans le catalogue",
        sectionInfo: 'Informations',
        saving: 'Enregistrement…', saveDraft: 'Enregistrer le brouillon', saveAndSend: 'Enregistrer et envoyer',
        savedAsDraft: 'Enregistré comme brouillon',
        review: 'Vérifier', reviewing: 'Vérification…',
        reviewNeedsLines: 'Ajoute au moins une ligne avant de vérifier',
        linesRequired: 'Ajoute au moins une ligne avec une description',
        badLine: 'Vérifie la quantité et le prix de « {name} »',
        newTitle: 'Nouveau devis', editTitle: 'Modifier le devis',
        newHint: 'Seul le titre est obligatoire ; le reste peut attendre',
        details: 'Informations du devis', notes: 'Notes',
        titleRequired: 'Le titre manque',
        created: 'Devis créé', saved: 'Devis enregistré',
      },
      fields: {
        customerPlaceholder: 'Entreprise ou particulier', paymentTermsPlaceholder: 'Ex. : 50 % à la commande, 50 % à la livraison',
        project: 'Projet', projectPlaceholder: 'À quel projet il appartient',
        title: 'Titre', titlePlaceholder: 'Ex. : Ligne de conditionnement 2 000 bouteilles/h',
        customer: 'Client', contact: 'Interlocuteur',
        date: 'Date',
        validUntil: 'Valable jusqu\'au', taxRate: 'TVA', paymentTerms: 'Conditions de paiement',
        notes: 'Notes internes', notesHint: 'Elles ne figurent pas sur le PDF',
        notesForClient: 'Observations pour le client', notesForClientHint: 'Imprimées sur le devis',
      },
      lines: {
        dragHint: 'Fais glisser un article du catalogue ici pour en faire une ligne',
        unit: 'Unité', subtotal: 'Sous-total', ai: 'IA', marginShort: 'Mg%',
        dragToReorder: 'Glisser pour réordonner', select: 'Sélectionner la ligne',
        editMargin: 'Clique pour modifier la marge', noCost: 'Aucun coût défini',
        manual: 'Ligne manuelle', describe: 'Que l’IA la rédige', describing: 'Rédaction…',
        annex: 'Annexe technique de ce poste',
        annexPlaceholder: 'Ce qui est fourni, comment c’est exécuté et ce qui n’entre pas. Cela figure dans l’annexe du PDF.',
        breakdown: 'Détail des matériaux ({count} composants)',
        addingComponentsTo: 'Ajout de composants à',
        untitledLine: 'Ligne sans nom',
        componentsAdded: '{n} ajouté(s) · dernier : {name}',
        componentsDone: 'Terminé',
        component: 'Composant / matériel', componentsTotal: 'Total composants',
        title: 'Lignes', description: 'Description', descriptionPlaceholder: 'Poste ou ensemble',
        quantity: 'Qté', unitPrice: 'Prix', costPrice: 'Coût', discount: 'Rem. %',
        margin: 'Marge', total: 'Total',
        add: 'Ajouter une ligne', addComponent: 'Ajouter un composant', remove: 'Supprimer',
        moveUp: 'Monter', moveDown: 'Descendre',
        components: '{count} composants', fromAi: 'IA', untitled: 'sans titre',
        fromComponents: 'Calculé à partir des composants',
        none: 'Aucune ligne pour le moment',
        noneHint: 'Ajoute-en une à la main, ou décris le chantier dans le panneau de droite.',
      },
      totals: {
        globalMargin: 'Marge globale', applyAll: 'Appliquer à tout',
        marginRequired: "Saisis un pourcentage de marge avant de l'appliquer",
        noPricedLines: 'Aucune ligne avec un prix auquel appliquer une marge',
        confirmGlobalMargin: 'La marge sera fixée à {percent} % sur {count} ligne(s), en écrasant leurs coûts. Continuer ?',
        title: 'Totaux', subtotal: 'Total HT', discount: 'Remises',
        tax: 'TVA', taxAt: 'TVA {rate} %', grandTotal: 'Total TTC', margin: 'Marge',
      },

      ai: {
        title: 'Rédiger avec l\'IA',
        intro: 'Décris le chantier et les lignes sont rédigées à partir du catalogue, des modèles et des règles de l\'entreprise.',
        describe: 'Ce qu\'il faut chiffrer',
        placeholder: 'Ex. : presse hydraulique 100 t avec armoire électrique, automate S7-1200 et mise en service',
        generateQuote: 'Un devis complet',
        generateAssembly: 'Un ensemble',
        addToSelected: 'Ajouter à « {name} »',
        proposalTitle: '{count} lignes proposées',
        grounded: '{pct} % à vos propres prix',
        apply: 'Ajouter {count} au devis',
        discard: 'Écarter',
        applied: '{count} lignes ajoutées',
        added: '{count} composants ajoutés',
        review: 'Vérifier ce qui manque',
        reviewClean: 'Rien d\'évident ne manque.',
        warning: {
          empty: 'Rien n\'est revenu. Essaie de décrire le chantier plus en détail.',
          truncated: 'La réponse a été coupée par la longueur. Découpe la demande en plusieurs parties.',
          unparseable: 'La réponse n\'est pas revenue dans un format exploitable. Réessaie.',
        },
      },

      preflight: {
        backAndFix: 'Revenir et corriger', fixFirst: 'Corrige les erreurs', sendAnyway: 'Envoyer quand même',
        summaryErrors: '{errors} erreur(s) et {warnings} avertissement(s)',
        summaryWarnings: "{warnings} avertissement(s), rien qui empêche l'envoi",
        summaryClean: 'Tout est en ordre',
        groups: { info: 'Informations', lines: 'Lignes', margin: 'Marges' },
        title: 'Avant l\'envoi',
        intro: 'Une vérification rapide. Les avertissements n\'empêchent pas l\'envoi ; les erreurs si.',
        items: {
          title: 'Titre du devis',
          customer: 'Client renseigné',
          validUntil: 'Date de validité',
          paymentTerms: 'Conditions de paiement',
          lines: '{count} lignes avec description et prix',
          emptyLines: '{count} lignes sans description ou à prix 0',
          negativeMargin: '{count} lignes dont le coût atteint ou dépasse le prix de vente',
          thinMargin: '{count} lignes sous {threshold} % de marge',
          averageMargin: 'Marge moyenne du devis : {margin} %',
          averageMarginHidden: 'Marge moyenne du devis (seuls ceux qui voient les coûts voient le chiffre)',
          unknownCost: '{count} lignes sans coût : leur marge réelle est inconnue',
        },
      },
      // Lo que el correo que lleva el presupuesto dice de entrada: el asunto y
      // la plantilla, en el idioma del cliente (QuotePage → composeEmail). El
      // resto lo escribe quien lo manda en el redactor de la plataforma.
      send: {
        tplSubject: {
          standard: 'Devis {code} · {title}',
          followUp: 'Relance du devis {code}',
        },
        greeting: 'Bonjour {contact},',
        greetingAnon: 'Bonjour,',
        tplBody: {
          standard: "Vous trouverez ci-joint le devis {code} pour {title}, d'un montant total de {total}.\n\nSi vous souhaitez revoir un point quelconque, dites-le-moi.",
          followUp: "Je reviens vers vous au sujet du devis {code} que nous vous avons envoyé, pour {total}. Dans l'attente de votre retour.",
        },
        validitySentence: "L'offre est valable jusqu'au {validUntil}.",
      },
      outcome: {
        title: 'Résultat',
        askTitle: 'Ce qu\'est devenu ce devis',
        askIntro: 'Le motif est facultatif, mais c\'est la seule chose qui permet au système d\'apprendre de ce qui se gagne et se perd.',
        reasonLabel: 'Motif', noReason: 'Non précisé', noteLabel: 'Commentaire',
        reason: {
          price: 'Prix', scope: 'Périmètre', timing: 'Délais',
          competitor: 'Concurrence', other: 'Autre',
        },
      },
      archive: {
        title: 'Archiver le devis',
        body: 'Tu vas archiver {code}. Rien n\'est supprimé : il disparaît des listes.',
        done: 'Devis archivé',
        restored: 'Devis remis dans les listes',
        isArchived: 'Ce devis est archivé : il ne figure sur aucune liste. Remettez-le pour y retravailler.',
      },

      pdfOptions: {
        emailTitle: 'Envoyer le devis par e-mail',
        emailIntro: 'La mise en forme du PDF joint, et la langue dans laquelle on écrit au client.',
        language: 'Langue du client',
        writeEmail: 'Rédiger l’e-mail',
        languages: { es: 'Espagnol', ca: 'Catalan', en: 'Anglais', fr: 'Français' },
        title: 'Générer le PDF',
        intro: "Choisissez ce que le document montre. La question est posée à chaque fois : un bureau d'études et un particulier ne lisent pas le même document.",
        generate: 'Générer le PDF',
        detail: {
          label: 'Ce que montre chaque poste',
          elements: 'Le poste et son contenu',
          elementsHelp: 'Le titre et, en dessous, les éléments qui le composent.',
          titles: 'Seulement le titre du poste',
          titlesHelp: 'Ni description ni éléments : une ligne par poste.',
        },
        prices: {
          label: 'Quels prix apparaissent',
          groups: 'Seulement celui de chaque poste',
          groupsHelp: 'Prix, remise et montant par poste. Les éléments, sans prix.',
          all: 'Tous, élément par élément',
          allHelp: 'Chaque élément avec son montant, dont la somme fait le prix unitaire du poste.',
          allNeedsElements: 'Il faut que les éléments soient affichés.',
          total: 'Seulement le total (prix fermé)',
          totalHelp: 'Aucun montant par poste. La base, la TVA et le total apparaissent toujours.',
        },
        annex: {
          label: "Annexe explicative de l'installation",
          help: 'Une feuille à part à la fin, poste par poste, avec ce que chacun comprend.',
        },
        summary: {
          detail: { elements: 'les postes avec leurs éléments', titles: 'seulement les titres des postes' },
          groups: 'Le document montrera {detail}, avec le montant de chaque poste{annex}.',
          all: 'Le document montrera {detail}, avec le montant de chaque élément et de chaque poste{annex}.',
          total: 'Le document montrera {detail}, sans montants par poste : seulement le total{annex}.',
          withAnnex: ", et l'annexe à la fin",
        },
      },
      project: {
        title: 'Projet',
        placeholder: 'Chercher le projet…',
        save: 'Enregistrer le lien',
        linked: 'Devis lié au projet',
        unlinked: 'Devis délié du projet',
      },
      customer: {
        placeholder: 'Cherchez un client, ou saisissez le nom',
        searching: 'Recherche…',
        noResults: "Aucun client de ce nom. Le nom sera enregistré tel quel.",
        linked: 'Fiche',
        unlink: 'Retirer le lien avec la fiche client',
      },
      guide: {
        catalog: {
          filters: "Cherchez par nom, référence ou fournisseur. Les deux listes restreignent par famille et par type : matériel, main-d'œuvre ou prestation.",
          table: 'La marge se calcule seule, à partir du prix de vente et du coût : en rouge, c’est presque toujours un coût saisi dans la case du prix. Cliquez sur une ligne pour la corriger.',
          form: "Le coût n'apparaît jamais sur le devis que lit le client : il sert à la marge, et à ce que l'IA ne propose pas en dessous. Laissez-le à zéro si vous ne le connaissez pas. Désactiver un article le retire du catalogue sans toucher aux devis qui l'utilisent déjà.",
        },
        templates: {
          what: "Un modèle est un ensemble déjà monté —composants et heures— qui entre dans un devis comme une seule ligne et emporte son coût. Pas besoin de les créer à la main : en marquant un devis comme accepté, il vous est proposé de l'enregistrer comme modèle.",
          cards: 'Chaque fiche résume ce qu’on regarde pour la réutiliser : combien de composants, combien d’heures, et avec quelle marge elle est sortie. Cliquez pour l’ouvrir et l’ajuster.',
        },
        rules: {
          what: "Une règle, c'est ce que votre technicien le plus expérimenté applique sans qu'on le lui demande. Elles sont lues avant chaque brouillon et voyagent avec la demande à l'IA : cet écran est là où on lui apprend le métier.",
          list: "Chaque règle se lit comme on l'explique à voix haute : quand elle s'applique, ce qu'il faut faire, et ce qui arrive si on l'oublie. Celles marquées « Apprise » ont été écrites par le module en regardant ce que vous corrigez, et elles se modifient ou se suppriment comme les vôtres.",
        },
        learning: {
          actions: "« Recalculer » reprend les preuves et refait cette liste. « Réindexer » reconstruit la recherche par sens du catalogue, des modèles et des règles. Aucun des deux ne change vos données.",
          metrics: 'Quatre chiffres pour une seule question : est-ce que cela s’améliore. « Prix non retouché » est celui qui y répond : combien de prix proposés personne n’a eu à corriger.',
          tabs: 'En attente, celles qui attendent votre avis. Appliquées et écartées restent visibles, pour savoir ce qui a été décidé et quand.',
          cards: "Chaque fiche dit ce qu'elle a observé, sur combien de cas — « Voir les N fois » ouvre les preuves — et ce qu'elle fera exactement si vous l'acceptez. Rien ne s'applique tout seul : ce bouton est le seul moment où le module écrit dans votre catalogue ou dans vos règles.",
        },
      },
      catalog: {
        pendingHandover: '{count} matériaux sont encore enregistrés ici. À mesure que le stock les enregistre, ces fiches se convertissent seules et cet avis disparaît.',
        fromInventory: "De l'entrepôt",
        materialsElsewhere: "Les matériaux sont gérés par le module de stock : ils apparaissent ici et sont chiffrables, mais ils se créent et se corrigent là-bas. Cet écran enregistre la main-d'œuvre et les prestations.",
        title: 'Catalogue de tarifs',
        subtitle: 'Ce que vous vendez et ce que cela vous coûte',
        newIn: { rates: 'Nouveau tarif', materials: 'Nouveau matériau' },
        editIn: { rates: 'Modifier le tarif', materials: 'Modifier le matériau' },
        formSubtitleRate: 'Ce que c’est et ce que ça coûte',
        groups: {
          rates: 'Tarifs',
          ratesHint: "Main-d'œuvre et prestations : l'heure d'un ouvrier, celle du chef d'atelier, un déplacement.",
          materials: 'Matériaux',
          materialsHint: 'Pièces et fournitures posées sur le chantier.',
        },
        category: 'Catégorie', allCategories: 'Toutes', kind: 'Type', allKinds: 'Tous',
        kinds: { material: 'Matériel', labour: 'Main-d\'œuvre', service: 'Prestation' },
        import: {
          open: 'Importer depuis Excel',
          title: 'Importer un tarif depuis Excel',
          file: 'Fichier',
          fileHint: 'Excel (.xlsx, .xls) ou CSV. Jusqu’à 8 Mo.',
          profile: 'Profil',
          profileHint: 'Comment ce fichier se lit. Enregistré une fois, il sert pour le tarif de chaque année.',
          newProfile: 'Nouveau : deviner les colonnes',
          lastFile: 'Dernier tarif importé avec ce profil : {name}.',
          download: 'Télécharger',
          empty: 'Choisissez le fichier du tarif',
          emptyText: 'On vous montre d’abord ce qui se passerait. Aucun prix ne change avant de cliquer sur Importer.',
          sheet: 'Feuille',
          sheetRows: '{name} ({n} lignes)',
          headerRow: 'Ligne d’en-têtes',
          headerRowHint: '0 pour la trouver automatiquement.',
          columns: 'Quelle colonne est chaque donnée',
          notMapped: '— Absente du fichier —',
          pricing: 'Prix',
          pricingHint: 'Si le tarif donne le prix public du fournisseur : sa remise donne votre coût, et votre marge donne le prix de vente. Une colonne de coût ou de vente l’emporte.',
          discount: 'Remise fournisseur (%)',
          markup: 'Votre marge sur le coût (%)',
          defaults: 'Pour ce que le fichier ne dit pas',
          decimal: 'Décimales',
          decimals: {
            auto: 'Détecter',
            comma: 'Virgule (1.234,56)',
            dot: 'Point (1,234.56)',
          },
          matchBy: 'Reconnaître l’existant par',
          match: {
            reference: 'Référence',
            name: 'Nom',
          },
          reading: 'Lecture du fichier…',
          count: {
            create: '{n} nouveaux',
            update: '{n} changent de prix ou de données',
            unchanged: '{n} identiques',
            errors: '{n} avec des problèmes',
            created: '{n} créés',
            updated: '{n} mis à jour',
          },
          missing: 'Ces colonnes du profil ne sont pas dans ce fichier : {fields}. Choisissez-en une autre ou laissez-la vide.',
          needsName: 'Choisissez quelle colonne est le nom de l’article.',
          rowNumber: 'Ligne',
          action: {
            create: 'Nouveau',
            update: 'Change',
            unchanged: 'Identique',
          },
          errorList: 'Voir les {n} lignes avec des problèmes',
          errors: {
            NO_NAME: 'A un prix mais pas de nom',
            NO_PRICE: 'N’a pas de prix',
            NEGATIVE_PRICE: 'Prix négatif',
            DUPLICATE_IN_FILE: 'Répétée dans le fichier (la dernière compte)',
            OWNED_BY_INVENTORY: 'Ce matériel appartient au stock : son prix se change là-bas',
            MATERIALS_OWNED_BY_INVENTORY: 'Sur cette instance, les matériels se créent dans le stock',
            other: 'Import impossible ({code})',
          },
          profileName: 'Nom du profil (ex. Tarif Aragón)',
          saveProfile: 'Enregistrer le profil',
          updateProfile: 'Mettre à jour le profil',
          profileSaved: 'Profil « {name} » enregistré',
          run: 'Importer {n}',
          done: 'Import terminé',
          fields: {
            reference: 'Référence',
            name: 'Nom',
            description: 'Description',
            category: 'Famille',
            unit: 'Unité',
            kind: 'Type',
            costPrice: 'Coût',
            price: 'Prix de vente',
            listPrice: 'Prix public fournisseur',
            supplier: 'Fournisseur',
            supplierRef: 'Réf. fournisseur',
            leadTimeDays: 'Délai (jours)',
            taxRate: 'TVA (%)',
          },
        },
        searchPlaceholder: 'Nom, référence ou fournisseur…',
        leadDays: '{days} jours',
        empty: 'Le catalogue est vide',
        emptyHint: 'C\'est le fichier qui fait qu\'un devis prend des minutes au lieu d\'un après-midi.',
        emptyFiltered: 'Aucun résultat', emptyFilteredHint: 'Essaie d\'enlever un filtre.',
        nameRequired: 'Le nom manque', saved: 'Article enregistré',
        deactivate: 'Désactiver',
        deactivateTitle: 'Désactiver l\'article',
        deactivateBody: '« {name} » n\'apparaîtra plus au catalogue. Les devis qui l\'utilisent ne changent pas.',
        deactivated: 'Article désactivé',
        formSubtitle: "Ce que c’est, ce que ça coûte et d’où ça vient",
        whatItIs: "Ce que c’est",
        whatItCosts: "Ce que ça coûte et à combien ça se vend",
        whereItComesFrom: "D’où ça vient",
        readOnlyNote: "Cette fiche est un miroir du magasin : elle se lit ici et se corrige là-bas. Deux endroits pour le même prix, c’est un endroit où le prix est faux.",
        notFound: "Cet article n’existe plus",
        leaveTitle: "Laisser le tarif à moitié rempli",
        leaveBody: "Ce qui est écrit sera perdu. Un tarif à moitié rempli ne s’enregistre pas tout seul.",
        leaveConfirm: "Partir sans enregistrer",
        fields: {
          name: 'Nom', description: 'Description', kind: 'Type', unit: 'Unité',
          price: 'Prix de vente', costPrice: 'Coût', costHint: 'Laisse 0 si vous ne le connaissez pas : mieux vaut cela qu\'une marge inventée',
          taxRate: 'TVA', reference: 'Référence interne',
          supplier: 'Fournisseur', supplierRef: 'Réf. fournisseur', leadTimeDays: 'Délai (jours)',
        },
        whoProvidesIt: 'Qui l’assure',
        whoProvidesItHint: 'Une prestation se sous-traite : qui la réalise et sous quel délai.',
        newCategory: 'Nouvelle famille…',
        newCategoryLabel: 'Nom de la famille',
        otherUnit: 'Autre…',
        otherUnitLabel: 'Saisis l’unité',
        unitHint: 'Elle est imprimée sur le devis, après le prix.',
        units: {
          hour: { value: 'h', label: 'h (heure)' },
          workday: { value: 'journée', label: 'journée (jour de travail)' },
          unit: { value: 'u.', label: 'u. (unité)' },
          day: { value: 'jour', label: 'jour' },
          km: { value: 'km', label: 'km' },
          metre: { value: 'm', label: 'm (mètre)' },
          sqm: { value: 'm²', label: 'm² (mètre carré)' },
          cbm: { value: 'm³', label: 'm³ (mètre cube)' },
          kg: { value: 'kg', label: 'kg (kilo)' },
          litre: { value: 'l', label: 'l (litre)' },
        },
      },

      templates: {
        edit: 'Modifier le modèle', create: 'Créer le modèle', saveChanges: 'Enregistrer',
        estimatedTotals: 'Totaux estimés', globalMargin: 'Marge globale',
        bom: 'Nomenclature', addManual: '+ Manuel', supplierRef: 'Fournisseur · Réf',
        searchCatalog: 'Chercher au catalogue', searchLabour: 'Chercher main-d\'œuvre',
        searchCatalogPlaceholder: 'Chercher une pièce, un composant, une prestation…',
        searchLabourPlaceholder: 'Chercher un poste ou une spécialité…',
        searchNoResults: 'Aucun résultat pour « {query} »',
        searchHint: 'Saisis au moins 2 caractères pour chercher',
        noComponents: 'Aucun composant — appuie sur « + Manuel » ou cherche au catalogue',
        noLabour: 'Aucun poste — appuie sur « + Poste » ou cherche au catalogue',
        addRole: '+ Poste',
        title: 'Modèles',
        subtitle: 'Machines et ensembles que vous avez déjà chiffrés',
        new: 'Nouveau modèle',
        formHint: 'Ce qui est enregistré ici sert de référence à l\'IA pour chiffrer quelque chose de similaire.',
        identity: 'Identification',
        searchPlaceholder: 'Nom ou description…',
        components: 'Composants', addComponent: 'Ajouter un composant',
        labour: 'Main-d\'œuvre', addLabour: 'Ajouter un poste',
        labourHint: 'Heures et coût horaire. Le prix de vente de l\'heure vient du multiplicateur des réglages.',
        hours: 'Heures', componentCost: 'Coût matériel', labourCost: 'Coût main-d\'œuvre',
        totalCost: 'Coût total', salePrice: 'Prix de vente',
        annex: 'Annexe technique',
        annexHint: 'Une description imprimable dans l\'annexe du devis.',
        annexDoc: 'Annexe de la machine',
        annexPhotoLater: 'Enregistre le modèle pour pouvoir y ajouter des photos.',
        fromQuote: 'Depuis un devis',
        empty: 'Aucun modèle pour le moment',
        emptyHint: 'En marquant un devis comme accepté, on vous proposera de le garder comme modèle.',
        emptyFiltered: 'Aucun résultat', emptyFilteredHint: 'Essaie d\'enlever un filtre.',
        nameRequired: 'Le nom manque', saved: 'Modèle enregistré', removed: 'Modèle supprimé',
        removeTitle: 'Supprimer le modèle', removeBody: 'Supprimer « {name} » ? Les devis qui en sont issus ne changent pas.',
        fields: {
          name: 'Nom', description: 'Description',
          namePlaceholder: 'Ex. : Conditionneuse flow pack 120 u/min',
          tags: 'Étiquettes (séparées par des virgules)', tagsHint: 'Ex. : conditionnement, flow pack, agroalimentaire',
          rolePlaceholder: 'Ex. : Ingénierie / Conception',
          descriptionHint: 'Mieux tu la décris, mieux l\'IA la retrouve',
          reference: 'Référence', component: 'Composant',
          role: 'Poste', hours: 'Heures', rate: 'Coût/h', cost: 'Coût',
        },
      },

      rules: {
        title: 'Règles techniques',
        subtitle: 'Les critères que vous appliquez en chiffrant',
        new: 'Nouvelle règle', edit: 'Modifier la règle',
        formHint: 'Écris-la comme tu l\'expliquerais à un nouveau : quand elle s\'applique, quoi faire, et ce qui arrive si on l\'oublie.',
        identity: 'Quelle règle c’est',
        body: 'Ce qu’elle dit',
        leaveTitle: 'Laisser la règle à moitié écrite',
        leaveBody: 'Ce qui est écrit sera perdu. Une règle à moitié rédigée ne s’enregistre pas toute seule.',
        leaveConfirm: 'Partir sans enregistrer',
        notFound: 'Cette règle n’existe plus',
        searchPlaceholder: 'Chercher dans les règles…',
        learned: 'Apprise',
        empty: 'Aucune règle pour le moment',
        emptyHint: 'C\'est ce que sait votre technicien le plus expérimenté et qui n\'est écrit nulle part.',
        emptyFiltered: 'Aucun résultat', emptyFilteredHint: 'Essaie d\'enlever un filtre.',
        required: 'Le nom ou la règle manque', saved: 'Règle enregistrée', removed: 'Règle supprimée',
        removeTitle: 'Supprimer la règle', removeBody: 'Supprimer « {title} » ?',
        fields: {
          title: 'Nom', titlePlaceholder: 'Ex. : Tirage de tuyauterie de plus de 30 m',
          context: 'Quand elle s\'applique', contextHint: 'La situation dans laquelle il faut en tenir compte',
          rule: 'Quoi faire', ruleHint: 'Ce qui est inclus, ajouté ou vérifié',
          consequence: 'Ce qui arrive si on l\'omet', example: 'Exemple',
        },
      },

      learning: {
        title: 'Apprentissage',
        subtitle: 'Ce que le système a observé dans vos devis, en attente de votre jugement',
        refresh: 'Recalculer', refreshed: 'Recalculé',
        reindex: 'Réindexer', reindexed: '{count} éléments réindexés',
        noSemantic: 'La recherche par sens est désactivée sur cette installation : la recherche se fait par texte. Tout fonctionne, mais trouve moins.',
        tabs: { pending: 'En attente', accepted: 'Appliquées', dismissed: 'Écartées' },
        metrics: {
          proposed: 'Lignes proposées', acceptance: 'Conservées',
          unedited: 'Prix inchangé', avgEdit: 'Correction moyenne',
        },
        kinds: {
          'price-adjust': 'Ajustement de prix',
          'new-rate-item': 'Absent du catalogue',
          'stop-proposing': 'Ne plus proposer',
          'template-from-quote': 'Nouveau modèle',
        },
        claims: {
          'price-adjust': 'Vous avez corrigé « {concept} » {samples} fois : l\'IA propose {avgProposed} et cela finit à {medianFinal} ({medianDeltaPct} %).',
          'new-rate-item': '« {name} » est saisi à la main dans {samples} devis et n\'est pas au catalogue. Prix habituel : {medianPrice}.',
          'stop-proposing': '« {concept} » a été proposé et retiré {rejected} fois, sans jamais être conservé.',
          'template-from-quote': 'Le devis {code} ({grandTotal}) a été gagné. Il peut servir de référence pour les suivants.',
        },
        effects: {
          'price-adjust': 'Met à jour le prix au catalogue',
          'new-rate-item': 'Crée l\'article au catalogue',
          'stop-proposing': 'Crée une règle technique',
          'template-from-quote': 'Crée le modèle',
        },
        evidence: 'Voir les {count} occurrences', rejectedTimes: 'retiré {count} fois',
        accept: 'Appliquer', dismiss: 'Écarter',
        accepted: 'Appliqué', dismissed: 'Écarté',
        empty: {
          pending: 'Rien à examiner',
          accepted: 'Vous n\'avez encore rien appliqué',
          dismissed: 'Rien d\'écarté',
        },
        emptyHint: {
          pending: 'Des choses apparaîtront ici quand le système verra un motif : un prix que vous corrigez toujours, quelque chose que vous saisissez à la main encore et encore.',
          accepted: 'Ce que vous acceptez est appliqué au catalogue ou aux règles et listé ici.',
          dismissed: 'Ce que vous écartez n\'est plus jamais proposé.',
        },
      },

      catalogPanel: {
        label: 'Catalogue', open: 'Ouvrir le catalogue', close: 'Masquer le catalogue',
        materials: 'Matériel', rates: 'Tarifs',
        search: 'Chercher dans le catalogue…', allCategories: 'Toutes les catégories',
        loading: 'Chargement…', noResults: 'Aucun résultat',
        dragHint: "Fais glisser « {name} » sur une ligne, ou clique pour l'ajouter",
        failed: "Le catalogue n'a pas pu être chargé.",
        retry: 'Réessayer',
        partial: 'Affichage de {shown} sur {total} articles. Cherchez dans le catalogue complet pour voir le reste.',
      },

      annexAi: {
        tab: 'Assistant', title: 'Assistant de l’annexe', status: 'Prêt', newChat: 'Recommencer',
        emptyTitle: 'Que doit dire cette annexe ?',
        emptyHint: 'Explique-le comme tu l’expliquerais à un collègue. Rédige tout le document, ou retouche celui qui est là.',
        placeholder: 'Rédige-moi l’annexe, ou dis-moi quoi changer…',
        tags: { write: 'Rédiger', table: 'Tableau', terms: 'Conditions', exclusions: 'Exclusions', tidy: 'Retoucher' },
        examples: {
          write: 'Rédige l’annexe d’une armoire de commande pour quatre moteurs avec variateur, API et pupitre.',
          writeFor: 'Rédige l’annexe de « {name} »',
          tableFrom: 'Ajoute un tableau de caractéristiques à partir de ses {n} composants',
          intro: 'Rédige la présentation et le périmètre de ce devis',
          terms: 'Rédige les conditions : délai, garantie et façon de travailler',
          table: 'Ajoute un tableau avec les caractéristiques principales.',
          exclusions: 'Ajoute les exclusions habituelles pour ce type de travaux.',
          shorter: 'Raccourcis le deuxième paragraphe et enlève le superflu.',
        },
        proposal: 'Propose {n} bloc(s)', apply: 'Appliquer', discard: 'Écarter', applied: 'Appliqué au document.',
        review: {
          title: 'Propose {n} bloc(s). Passe-les en revue :',
          all: 'Tout cocher', none: 'Tout décocher',
          apply: 'Appliquer {n} au document',
          applied: '{n} bloc(s) appliqués au document.',
          help: 'Ce que tu coches devient le document. Ce que tu décoches ne reste pas, et tu pourras tout retoucher ensuite dans la colonne du document.',
          state: { new: 'Nouveau', changed: 'Modifié', same: 'Inchangé' },
        },
        done: 'Terminé.',
        warnings: {
          truncated: "Ça a été coupé en cours de route : ce que tu as demandé ne tient pas d'un coup. Demande-le en plusieurs fois — d'abord ce qui est fourni, puis les caractéristiques — ou raccourcis l'instruction.",
          unparseable: "La réponse est arrivée cassée et n'a pas pu être utilisée. Réessaie.",
          empty: "Rien n'est sorti. Explique-le-moi autrement.",
        },
      },
      annexDoc: {
        title: 'Annexe technique',
        intro: 'Présentation et étendue',
        closing: 'Conditions et exclusions',
        saveFirst: 'Enregistre le devis pour pouvoir y ajouter des photos.',
      },

      annex: {
        title: 'Annexe technique',
        edit: 'Modifier', write: 'Rédiger une annexe',
        blocks: '{n} bloc(s)',
        emptyCta: 'Pas encore d’annexe. Clique pour la rédiger.',
        writing: 'Document', resize: 'Fais glisser pour répartir ; double clic remet tout',
        asPrinted: 'Tel qu’il s’imprimera',
        panels: { hide: 'Fermer « {name} »', show: 'Ouvrir « {name} »', reopen: 'Fermées' },
        rendering: 'Dessin…', emptyPreview: 'Rien à montrer pour l’instant.',
        vars: {
          title: 'Valeurs à renseigner',
          hint: {
            template: 'La même annexe sert pour la machine de 15 kW et pour celle de 400. Écris la clé entre doubles accolades dans le texte et déclare-la ici.',
            quote: 'Les valeurs que demande cette annexe. Ce que tu laisses vide s’imprime avec les accolades.',
          },
          add: '+ Valeur', key: 'clé', label: 'Libellé', unit: 'Unité',
          none: 'Cette annexe ne demande aucune donnée.',
          loose: 'Le texte utilise des champs non déclarés :',
          looseAnswer: 'Champs écrits dans le texte. Remplissez-les ici ; ce qui reste vide s\'imprime avec ses accolades.',
        },
        auto: {
          title: 'Données du devis qui se remplissent seules',
          hint: 'Cliquez sur l\'une pour la copier et collez-la dans le texte. À l\'impression, elle devient la valeur de ce devis.',
          copied: '{hole} copié. Collez-le dans le texte.',
          groups: { quote: 'Devis', customer: 'Client', company: 'Votre entreprise' },
          keys: {
            quote_code: 'Code',
            quote_title: 'Titre',
            quote_date: 'Date',
            quote_validUntil: 'Valable jusqu\'au',
            quote_subtotal: 'Base HT',
            quote_total: 'Total',
            quote_paymentTerms: 'Conditions de paiement',
            customer_name: 'Nom',
            customer_contact: 'Contact',
            company_name: 'Nom',
            company_taxId: 'N° TVA',
            company_phone: 'Téléphone',
            company_email: 'E-mail',
            today: 'Date du jour',
          },
        },
        types: { heading: 'Titre', paragraph: 'Paragraphe', list: 'Liste', table: 'Tableau', image: 'Photo' },
        up: 'Monter', down: 'Descendre', remove: 'Retirer',
        empty: 'L’annexe est vide. Ajoute un bloc pour commencer.',
        headingPlaceholder: 'Étendue de la fourniture',
        paragraphPlaceholder: 'Ce qui est fourni, comment c’est exécuté et ce qui n’entre pas.',
        itemPlaceholder: 'Une condition, une exclusion…', addItem: '+ Ligne',
        header: 'Colonne', addRow: '+ Ligne', addColumn: '+ Colonne', removeColumn: '− Colonne',
        noPhoto: 'Sans photo', upload: 'Téléverser', uploading: 'Envoi…', choose: 'En choisir une déjà envoyée',
        captionPlaceholder: 'Légende',
      },
      composer: {
        resize: 'Fais glisser pour changer la largeur ; double-clic pour la rétablir',
        tabs: 'Ce qui est affiché',
        tab: { quote: 'Devis', catalog: 'Catalogue', assistant: 'Assistant' },
        replies: {
          done: 'Prêt : {n} ligne(s) préparées. Coche celles que tu veux et ajoute-les.',
          empty: "Je n'ai rien pu préparer. Essaie de le décrire un peu plus en détail.",
          truncated: 'La réponse a été coupée en cours de route : le chantier est long. Demande-le-moi par parties, ou décris-le plus court.',
          retry: 'Je n\'ai pas su produire une réponse utilisable. Réessaie, et si cela persiste, décris-le autrement.',
          'no-credit': 'Le compte d’IA de cette instance n’a plus de crédit, je ne peux donc rien rédiger. Préviens qui la gère : tant qu’il n’est pas rechargé, réessayer ne servira à rien.',
          'no-target': 'Je ne trouve pas cette ligne dans le devis. Dis-moi son nom exact.',
          'no-selection': "Sélectionne d'abord une ligne avec le point à sa gauche.",
          'empty-quote': "Le devis est vide : il n'y a rien dont faire une variante.",
          idle: 'Que puis-je faire pour ce devis ?',
        },
        label: 'Assistant', open: "Ouvrir l'assistant", collapse: 'Masquer',
        expand: 'Agrandir', restore: 'Restaurer',
        title: 'Assistant de devis', status: 'Prêt',
        newChat: 'Nouvelle conversation',
        emptyTitle: "Qu'est-ce qu'il faut chiffrer ?",
        emptyHint: "Décris-le comme tu l'expliquerais à un collègue. Les lignes sont rédigées à partir de votre catalogue, vos modèles et vos règles.",
        placeholder: "Décris la machine, l'ensemble, ou ce qu'il faut ajouter…",
        selected: 'Sélectionnée : <strong>{name}</strong>',
        selectedHint: '— dis-lui quoi en faire',
        selectedOf: '{n} sur {total} sélectionnées',
        all: 'Tout', none: 'Rien',
        include: 'Inclure cette ligne',
        parts: '{n} composants',
        fromTemplate: 'Trouvé dans vos modèles',
        willAddTo: 'Sera ajouté dans « {name} »',
        addToQuote: 'Ajouter au devis',
        replaceVariant: 'Remplacer par cette variante',
        added: 'Ajouté au devis',
        thinking: {
          start: 'Réflexion…', reading: 'Lecture de ta demande…',
          catalogue: 'Recherche dans le catalogue…', knowledge: 'Consultation des modèles et des règles…',
          pricing: 'Calcul des prix et des marges…', writing: 'Préparation de la proposition…',
        },
        examples: {
          wholeLabel: 'Machine',
          whole: "Rédige le devis d'une presse hydraulique de 150 t avec automate et pupitre HMI",
          templateLabel: 'Modèle',
          template: 'Ajoute la machine standard que nous faisons habituellement',
          assemblyLabel: 'Ensemble',
          assembly: 'Armoire électrique : automate, 6 bornes 2,5 mm², rail DIN et goulotte',
          addLabel: 'Ajouter',
          add: 'Ajoute les lignes de projet technique, mise en conformité et mise en service',
          wholeReal: 'Chiffre « {name} »',
          templateReal: 'Ajoute « {name} » au devis',
          assemblyReal: 'Monte un ensemble avec {items}',
          addReal: 'Ajoute {items}',
        },
      },
      review: {
        title: 'Revue technique', subtitle: "Ce qui pourrait manquer avant l'envoi",
        working: 'Analyse du devis…',
        clean: "Le devis semble complet : rien d'évident ne manque.",
        addSuggested: 'Ajouter « {name} »',
      },
      search: { subtitle: 'Ouvrir le devis' },
      pdf: { taxId: 'N° fiscal' },
      notifications: {
        categories: {
          expired: 'Un devis a expiré',
          accepted: 'Un devis a été accepté',
        },
      },
    },

    modules: { quotes: 'Devis' },
    assistant: {
      openers: {
        quotes: {
          'how-are-quotes-going': 'Comment vont nos devis ce mois-ci ?',
          'waiting-for-an-answer': 'Quels devis attendent encore une réponse ?',
          'draft-a-quote': 'Prépare-moi un devis pour ce que je vais décrire',
        },
      },
    },
    permissions: {
      quotes: {
        read: 'Voir les devis, le catalogue et les modèles',
        write: 'Créer et modifier des devis, et utiliser l\'IA',
        costs: 'Voir et modifier les coûts et les marges',
        send: 'Envoyer un devis à un client',
        catalog: 'Modifier les prix du catalogue',
        knowledge: 'Modifier modèles, règles et apprentissage',
        import: 'Importer le tarif depuis un fichier Excel',
      },
    },
    settings: {
      ns: {
        quotes: {
          title: 'Devis',
          subtitle: 'Comment cette entreprise chiffre',
          acceptFromPortal: {
            label: 'L\'acceptation par le client dans son portail clôt le devis',
            help: 'Sa décision change le statut et l\'annonce, comme si quelqu\'un d\'ici avait cliqué. N\'a d\'effet qu\'avec le portail client installé.',
          },
          trade: {
            label: 'Ce que cette entreprise fabrique ou installe',
            help: 'Deux ou trois lignes avec vos mots. C\'est ce qui fait que l\'IA sait de quoi vous parlez.',
          },
          defaultTaxRate: { label: 'TVA par défaut d\'un nouveau devis' },
          defaultCurrency: { label: 'Devise des devis', help: 'ISO 4217, trois lettres.' },
          defaultValidityDays: {
            label: 'Durée de validité d\'un devis, en jours',
            help: 'Passée cette date il est marqué expiré tout seul. Mets 0 pour qu\'ils n\'expirent pas.',
          },
          defaultPaymentTerms: { label: 'Conditions de paiement par défaut' },
          minMarginPct: {
            label: 'Marge en dessous de laquelle on alerte avant l\'envoi',
            help: 'Une alerte seulement : elle n\'empêche jamais d\'envoyer, elle s\'assure que vous l\'avez vue.',
          },
          labourMarkup: {
            label: 'Multiplicateur du coût d\'une heure vers son prix de vente',
            help: '1,5 signifie qu\'une heure coûtant 40 est vendue 60.',
          },
        },
      },
    },
    audit: {
      actions: {
        quotes: {
          quote_created: 'Devis créé',

          quote_draft: 'Devis remis en brouillon',
          quote_updated: 'Devis modifié',
          quote_sent: 'Devis envoyé',
          quote_emailed: 'Devis envoyé par courriel',
          quote_accepted: 'Devis accepté',
          quote_linked_to_project: 'Devis lié à un projet',
          quote_rejected: 'Devis refusé',
          quote_expired: 'Devis expiré',
          quote_cancelled: 'Devis annulé',
          quote_duplicated: 'Devis dupliqué',
          quote_archived: 'Devis archivé',
          quote_restored: 'Devis remis dans les listes',
          rate_item_created: 'Article ajouté au catalogue',
          rate_item_updated: 'Article du catalogue modifié',
          rate_item_deactivated: 'Article désactivé',
          rate_items_imported: 'Tarif importé depuis un fichier',
          import_profile_created: 'Profil d’import créé',
          import_profile_updated: 'Profil d’import modifié',
          import_profile_deleted: 'Profil d’import supprimé',
          template_created: 'Modèle créé',
          template_updated: 'Modèle modifié',
          template_deleted: 'Modèle supprimé',
          rule_created: 'Règle technique créée',
          rule_updated: 'Règle technique modifiée',
          rule_deleted: 'Règle technique supprimée',
          suggestion_accepted: 'Suggestion d\'apprentissage appliquée',
          suggestion_dismissed: 'Suggestion d\'apprentissage écartée',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'
// Lo que dicen las tarjetas del asistente (backend/assistant.js): otro contrato,
// en su propio fichero por la misma razón.
import { withAssistantTools } from './i18n/assistant.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAssistantTools(withAnalytics(baseMessages, 'quotes'), 'quotes')))

export default { routes, navigation, messages }
